import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LineChart, Line } from "recharts";
import { format } from "date-fns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { BookOpen, GraduationCap, Award, TrendingUp, Users, Calendar as CalendarIcon, Clock, BarChart3, FileText, Plus, Edit, Trash2, Eye, AlertTriangle, Download, Settings, Save, CheckCircle, Check, Target, Zap, ArrowLeft } from "lucide-react";
import { X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import Adminheader from "./Header";
import Adminsidebar from "./Sidebar";
import { db, auth } from "../firebase/config";
import { collection, onSnapshot, doc, setDoc, updateDoc, getDoc, addDoc, deleteDoc, serverTimestamp, arrayUnion } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";


// Import new grading components
import GradingStructure from "./GradingStructure";
import PerformanceAnalytics from "./PerformanceAnalytics";
import RankingSystem from "./RankingSystem";
import ReportGeneration from "./ReportGeneration";

import ExamSessionManagement from "./ExamSessionManagement";
import SharedGradingDashboard from "./shared/SharedGradingDashboard";

import ReportPreview from "./Headerfiles/ReportPreview";
import { SAMPLE_DATA, TEMPLATES, ReportTemplate } from "./Headerfiles/types";
import { getEventColor } from "@/lib/eventHelpers";

// Type definitions for better type safety
interface TimetableEntry {
  teacherId: string;
  teacherName: string;
  classId: string;
  className: string;
  subjectId: string;
  subjectName: string;
}

interface SubjectAnalysis {
  subjectId: string;
  subjectName: string;
  actual: number;
  target: number;
}

interface TeacherAvailability {
  name: string;
  subject: string;
  actualLessons: number;
  assignedLessons: number;
}

interface TeacherConflict {
  type: string;
  teacherId: string;
  teacherName: string;
  timeSlot: string;
  assignments: Array<{
    classId: string;
    className: string;
    subjectName: string;
  }>;
  severity: string;
}

interface Notification {
  id: number;
  message: string;
  type: string;
  timestamp: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  type: 'exam' | 'meeting' | 'event' | 'holiday' | string;
  description: string;
  createdAt?: any;
}

interface Term {
  id: string;
  number: number;
  title: string;
  openingDate: Date | undefined;
  closingDate: Date | undefined;
}

const AcademicsContent = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  //const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('weekly');
 // const [timetableView, setTimetableView] = useState("weekly");
  const [gradingView, setGradingView] = useState("structure");
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate>('default');
  const [isApplyThemeDialogOpen, setIsApplyThemeDialogOpen] = useState(false);

  // Exam Category Management State
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [categorySubjectId, setCategorySubjectId] = useState("");
  const [categoryClassId, setCategoryClassId] = useState<string[]>([]);
  const [categoryScope, setCategoryScope] = useState<"global"|"class">("global");
  const [categoryList, setCategoryList] = useState<{ id?: string; name: string; maxMarks: number; weight: number }[]>([]);

  
  // Timetable modes and state
  const [timetableMode, setTimetableMode] = useState("view"); // "view" or "edit"
  const [selectedClassForView, setSelectedClassForView] = useState<string>("");
  const [currentEditingClass, setCurrentEditingClass] = useState<string>("");
  const [savedTimetables, setSavedTimetables] = useState<Record<string, Record<string, TimetableEntry>>>({});
  const [publishedTimetables, setPublishedTimetables] = useState<Record<string, any>>({});
  //const [showGradeEntry, setShowGradeEntry] = useState(false);

  // NEW: Conflict detection and analysis state
  const [isConflictAnalysisOpen, setIsConflictAnalysisOpen] = useState(false);
  const [subjectTargets, setSubjectTargets] = useState<Record<string, { target: number }>>({});
  const [teacherLoads, setTeacherLoads] = useState<Record<string, number>>({});
  const [conflictNotifications, setConflictNotifications] = useState<Notification[]>([]);
  const [teacherEditMode, setTeacherEditMode] = useState(false);
  const [teacherSearch, setTeacherSearch] = useState("");
  const [lastEditedInfo, setLastEditedInfo] = useState<Record<string, { classId: string; className: string; lastEditedAt: any; editedBy: string }>>({});

  // Firebase states
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [networkError, setNetworkError] = useState<boolean>(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Dynamic data from Firestore
  const [sessions, setSessions] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);

  //const [timetable, setTimetable] = useState({});
  const [selectedCell, setSelectedCell] = useState<{sessionId: number, day: string} | null>(null);
  const [isAssignmentOpen, setIsAssignmentOpen] = useState(false);
  const [isSessionManagerOpen, setIsSessionManagerOpen] = useState(false);
  const [filterClass] = useState("all");
  const [filterTeacher, setFilterTeacher] = useState("all");
  const [filterSubject, setFilterSubject] = useState("all");

  // Calendar events from Firestore
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);

  // Term and Event Management
  const [selectedTerm, setSelectedTerm] = useState<string>("");
  const [terms, setTerms] = useState<Term[]>([]);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [eventForm, setEventForm] = useState({ title: "", type: "", date: undefined as Date | undefined, description: "" });
  const [useRecommendedDates, setUseRecommendedDates] = useState(false);
  const [loadingRecommendedDates, setLoadingRecommendedDates] = useState(false);

  // Event form state
  //const [isAddEventOpen, setIsAddEventOpen] = useState(false);
  const [newSession, setNewSession] = useState({ name: '', startTime: '', endTime: '', isBreak: false });
  const [editingSession, setEditingSession] = useState<{id: number, name: string, startTime: string, endTime: string, isBreak: boolean} | null>(null);
  
  const [showPastEvents, setShowPastEvents] = useState(false);

  const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

  // Authentication logic
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Authenticating user with uid:`, user.uid);
          await user.getIdToken(true);
          const idTokenResult = await user.getIdTokenResult(true);
          if (!idTokenResult) {
            throw new Error("Failed to retrieve user token claims");
          }
          const affiliations = (idTokenResult.claims.affiliations as { role: string; schoolID?: string }[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as { role: string; schoolID?: string } | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] ID Token Result claims:`, JSON.stringify(idTokenResult.claims, null, 2));

          let selectedAffiliation: { role: string; schoolID?: string } | null = null;
          if (defaultAffiliation && ['school_staff', 'admin'].includes(defaultAffiliation.role) && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => ['school_staff', 'admin'].includes(aff.role) && aff.schoolID) || affiliations[0];
          }

          if (selectedAffiliation?.role && ['school_staff', 'admin'].includes(selectedAffiliation.role) && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
            setSchoolID(selectedAffiliation.schoolID);
            setHasValidClaims(true);
            setNetworkError(false);
          } else {
            console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Invalid or missing claims, retrying after 10 seconds...`);
            await new Promise(resolve => setTimeout(resolve, 10000));
            await user.getIdToken(true);
            const retryIdTokenResult = await user.getIdTokenResult(true);
            const retryAffiliations = (retryIdTokenResult.claims.affiliations as { role: string; schoolID?: string }[] | undefined) || [];
            const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as { role: string; schoolID?: string } | undefined;
            const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

            let retrySelectedAffiliation: { role: string; schoolID?: string } | null = null;
            if (retryDefaultAff && ['school_staff', 'admin'].includes(retryDefaultAff.role) && retryDefaultAff.schoolID) {
              retrySelectedAffiliation = retryDefaultAff;
            } else if (retryAffiliations.length > 0) {
              retrySelectedAffiliation = retryAffiliations.find(aff => ['school_staff', 'admin'].includes(aff.role) && aff.schoolID) || retryAffiliations[0];
            }

            if (retrySelectedAffiliation?.role && ['school_staff', 'admin'].includes(retrySelectedAffiliation.role) && retrySelectedAffiliation.schoolID && retrySchoolID === retrySelectedAffiliation.schoolID) {
              setSchoolID(retrySelectedAffiliation.schoolID);
              setHasValidClaims(true);
              setNetworkError(false);
            } else {
              setHasValidClaims(false);
              setSchoolID(null);
              setNetworkError(false);
              toast({
                title: "Permission Error",
                description: "You lack the necessary permissions to view academics. Please contact support.",
                variant: "destructive",
                duration: 5000,
              });
            }
          }
        } catch (error: any) {
          console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error verifying user claims:`, error);
          if (error.code === 'auth/network-request-failed' || error.message.includes('network')) {
            setNetworkError(true);
            toast({
              title: "Network Error",
              description: "Failed to verify permissions due to a network issue. Please check your internet connection and try again.",
              variant: "destructive",
              duration: 5000,
            });
          } else {
            setHasValidClaims(false);
            setSchoolID(null);
            setNetworkError(false);
            toast({
              title: "Authentication Error",
              description: "Failed to verify permissions. Please try again or contact support.",
              variant: "destructive",
              duration: 5000,
            });
          }
        }
      } else {
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] No user logged in`);
        setNetworkError(false);
        toast({
          title: "Authentication Error",
          description: "No user logged in. Please log in and try again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsLoading(false);
    });

    return () => {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from onAuthStateChanged`);
      unsubscribe();
    };
  }, [navigate, toast]);

  // Fetch classes
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Skipping classes fetch: schoolID=${schoolID}, isLoading=${isLoading}, hasValidClaims=${hasValidClaims}`);
      return;
    }

    const classesRef = collection(db, `schools/${schoolID}/classes`);
    const unsubscribeClasses = onSnapshot(classesRef, (snapshot) => {
      const classList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
      }));
      setClasses(classList.sort((a, b) => a.name.localeCompare(b.name)));
      if (classList.length > 0 && !selectedClassForView) {
        setSelectedClassForView(classList[0].id);
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Set selectedClass to:`, classList[0].id);
      }
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Classes fetched:`, classList);
    }, (error) => {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error fetching classes:`, error);
      toast({
        title: "Error",
        description: "Failed to fetch classes. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      setClasses([]);
    });

    return () => {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from classes onSnapshot`);
      unsubscribeClasses();
    };
  }, [schoolID, isLoading, hasValidClaims, selectedClassForView, toast]);

  // Fetch teachers
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Skipping teachers fetch: schoolID=${schoolID}, isLoading=${isLoading}, hasValidClaims=${hasValidClaims}`);
      return;
    }

    const teachersRef = collection(db, `schools/${schoolID}/teachers`);
    const unsubscribeTeachers = onSnapshot(teachersRef, (snapshot) => {
      const teacherList = snapshot.docs.map(doc => {
        const data = doc.data();
        const middleName = data.middleName || '';
        const title = data.title || '';
        const employeeId = data.employeeId || '';
        const nameParts = [data.firstName, middleName, data.lastName].filter(Boolean);
        const fullName = nameParts.join(' ');
        const displayName = title ? `${title} ${fullName}` : fullName;
        
        // Debug log for each teacher
        console.log(`Teacher: ${doc.id}`, {
          firstName: data.firstName,
          lastName: data.lastName,
          title: data.title || 'NO TITLE',
          employeeId: data.employeeId || 'NO EMPLOYEE ID',
          displayName: displayName,
          finalOutput: `${displayName}${employeeId ? ` (${employeeId})` : ''}`
        });
        
        return {
          id: doc.id,
          name: displayName,
          employeeId: employeeId,
          subject: data.subjectsOfExpertiseId && data.subjectsOfExpertiseId.length > 0 ? 'Multiple Subjects' : null,
        };
      });
      setTeachers(teacherList);
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Teachers fetched:`, teacherList);
    }, (error) => {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error fetching teachers:`, error);
      toast({
        title: "Error",
        description: "Failed to fetch teachers. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      setTeachers([]);
    });

    return () => {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from teachers onSnapshot`);
      unsubscribeTeachers();
    };
  }, [schoolID, isLoading, hasValidClaims, toast]);

  // Fetch subjects
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Skipping subjects fetch: schoolID=${schoolID}, isLoading=${isLoading}, hasValidClaims=${hasValidClaims}`);
      return;
    }

    const subjectsRef = collection(db, `schools/${schoolID}/subjects`);
    const unsubscribeSubjects = onSnapshot(subjectsRef, (snapshot) => {
      const subjectList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
      }));
      setSubjects(subjectList);
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Subjects fetched:`, subjectList);
    }, (error) => {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Error fetching subjects:`, error);
      toast({
        title: "Error",
        description: "Failed to fetch subjects. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      setSubjects([]);
    });

    return () => {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' })}] Unsubscribing from subjects onSnapshot`);
      unsubscribeSubjects();
    };
  }, [schoolID, isLoading, hasValidClaims, toast]);

  // Fetch sessions - draft when editing, published when viewing
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) return;
    
    const collectionName = timetableMode === "edit" ? "sessionsDraft" : "sessionsPublished";
    const sessionsRef = doc(db, `schools/${schoolID}/config/${collectionName}`);
    
    const unsubscribe = onSnapshot(sessionsRef, async (docSnap) => {
      if (!docSnap.exists()) {
        const defaultSessions = [
          { id: 1, name: "Period 1", startTime: "08:00", endTime: "09:00", isBreak: false },
          { id: 2, name: "Period 2", startTime: "09:00", endTime: "10:00", isBreak: false },
          { id: 3, name: "Break", startTime: "10:00", endTime: "10:30", isBreak: true },
          { id: 4, name: "Period 3", startTime: "10:30", endTime: "11:30", isBreak: false },
          { id: 5, name: "Period 4", startTime: "11:30", endTime: "12:30", isBreak: false },
          { id: 6, name: "Lunch", startTime: "12:30", endTime: "13:30", isBreak: true },
          { id: 7, name: "Period 5", startTime: "13:30", endTime: "14:30", isBreak: false },
          { id: 8, name: "Period 6", startTime: "14:30", endTime: "15:30", isBreak: false },
        ];
        // Initialize both draft and published with defaults
        await setDoc(doc(db, `schools/${schoolID}/config/sessionsDraft`), { sessions: defaultSessions }, { merge: true });
        await setDoc(doc(db, `schools/${schoolID}/config/sessionsPublished`), { sessions: defaultSessions }, { merge: true });
        setSessions(defaultSessions);
        return;
      }
      setSessions(docSnap.data().sessions || []);
    }, (error) => {
      console.error("Error fetching sessions:", error);
      toast({ title: "Error", description: "Failed to fetch sessions.", variant: "destructive" });
    });
    return () => unsubscribe();
  }, [schoolID, isLoading, hasValidClaims, timetableMode, toast]);

  // Fetch subject targets
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) return;
    const targetsRef = doc(db, `schools/${schoolID}/config/subjectTargets`);
    const unsubscribe = onSnapshot(targetsRef, (docSnap) => {
      if (docSnap.exists()) {
        setSubjectTargets(docSnap.data() as Record<string, { target: number }>);
      } else {
        setSubjectTargets({});
      }
    }, (error) => {
      console.error("Error fetching targets:", error);
      toast({ title: "Error", description: "Failed to fetch targets.", variant: "destructive" });
    });
    return () => unsubscribe();
  }, [schoolID, isLoading, hasValidClaims, toast]);

  // Set default subject targets
  useEffect(() => {
    if (subjects.length > 0 && Object.keys(subjectTargets).length === 0 && schoolID) {
      const defaultTargets = subjects.reduce((acc, s) => ({ ...acc, [s.id]: { target: 5 } }), {});
      setDoc(doc(db, `schools/${schoolID}/config/subjectTargets`), defaultTargets).catch(console.error);
      setSubjectTargets(defaultTargets);
    }
  }, [subjects, schoolID]);

  // Fetch teacher loads
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) return;
    const loadsRef = doc(db, `schools/${schoolID}/config/teacherLoads`);
    const unsubscribe = onSnapshot(loadsRef, (docSnap) => {
      if (docSnap.exists()) {
        setTeacherLoads(docSnap.data() as Record<string, number>);
      } else {
        setTeacherLoads({});
      }
    }, (error) => {
      console.error("Error fetching teacher loads:", error);
      toast({ title: "Error", description: "Failed to fetch teacher loads.", variant: "destructive" });
    });
    return () => unsubscribe();
  }, [schoolID, isLoading, hasValidClaims, toast]);

  // Set default teacher loads
  useEffect(() => {
    if (teachers.length > 0 && Object.keys(teacherLoads).length === 0 && schoolID) {
      const defaults = teachers.reduce((acc, t) => ({ ...acc, [t.id]: 25 }), {});
      setDoc(doc(db, `schools/${schoolID}/config/teacherLoads`), defaults).catch(console.error);
      setTeacherLoads(defaults);
    }
  }, [teachers, schoolID]);

  // Initialize draft sessions from published when entering edit mode
  useEffect(() => {
    if (!schoolID || timetableMode !== "edit" || isLoading || !hasValidClaims) return;
    
    const initializeDraftSessions = async () => {
      try {
        const draftRef = doc(db, `schools/${schoolID}/config/sessionsDraft`);
        const publishedRef = doc(db, `schools/${schoolID}/config/sessionsPublished`);
        
        const draftDoc = await getDoc(draftRef);
        
        // If no draft exists, copy from published
        if (!draftDoc.exists()) {
          const publishedDoc = await getDoc(publishedRef);
          if (publishedDoc.exists()) {
            await setDoc(draftRef, publishedDoc.data());
          }
        }
      } catch (error) {
        console.error("Error initializing draft sessions:", error);
      }
    };
    
    initializeDraftSessions();
  }, [schoolID, timetableMode, isLoading, hasValidClaims]);

  // Fetch last edited info for all classes
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims || timetableMode !== "edit") return;
    const unsubscribes: (() => void)[] = [];
    
    classes.forEach((cls) => {
      const draftRef = doc(db, `schools/${schoolID}/timetableDrafts`, cls.id);
      const unsubscribe = onSnapshot(draftRef, (docSnap) => {
        if (docSnap.exists() && docSnap.data().lastEditedAt) {
          setLastEditedInfo(prev => ({
            ...prev,
            [cls.id]: {
              classId: cls.id,
              className: cls.name,
              lastEditedAt: docSnap.data().lastEditedAt,
              editedBy: docSnap.data().editedBy || 'Unknown'
            }
          }));
        }
      }, (error) => {
        console.error("Error fetching last edited info:", error);
      });
      unsubscribes.push(unsubscribe);
    });
    
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [schoolID, isLoading, hasValidClaims, timetableMode, classes]);

  // Fetch draft timetables for editing
  useEffect(() => {
    if (!schoolID || !currentEditingClass || isLoading || !hasValidClaims) return;
    const draftRef = doc(db, `schools/${schoolID}/timetableDrafts`, currentEditingClass);
    const unsubscribe = onSnapshot(draftRef, (docSnap) => {
      if (docSnap.exists()) {
        setSavedTimetables(prev => ({ ...prev, [currentEditingClass]: docSnap.data().schedule || {} }));
      } else {
        setSavedTimetables(prev => ({ ...prev, [currentEditingClass]: {} }));
      }
    }, (error) => {
      console.error("Error fetching draft:", error);
    });
    return () => unsubscribe();
  }, [schoolID, currentEditingClass, isLoading, hasValidClaims]);

  // Fetch published timetables for viewing
  useEffect(() => {
    if (!schoolID || !selectedClassForView || isLoading || !hasValidClaims) return;
    const publishedRef = doc(db, `schools/${schoolID}/timetablePublished`, selectedClassForView);
    const unsubscribe = onSnapshot(publishedRef, (docSnap) => {
      if (docSnap.exists()) {
        setPublishedTimetables(prev => ({ ...prev, [selectedClassForView]: docSnap.data() }));
      }
    }, (error) => {
      console.error("Error fetching published:", error);
    });
    return () => unsubscribe();
  }, [schoolID, selectedClassForView, isLoading, hasValidClaims]);

  // Fetch calendar events
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) return;
    const eventsRef = collection(db, `schools/${schoolID}/events`);
    const unsubscribe = onSnapshot(eventsRef, (snapshot) => {
      const eventsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CalendarEvent));
      setCalendarEvents(eventsList);
    }, (error) => {
      console.error("Error fetching events:", error);
      toast({ title: "Error", description: "Failed to fetch events.", variant: "destructive" });
    });
    return () => unsubscribe();
  }, [schoolID, isLoading, hasValidClaims, toast]);

  // Fetch terms
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims) return;
    const termsRef = collection(db, `schools/${schoolID}/terms`);
    const unsubscribe = onSnapshot(termsRef, (snapshot) => {
      const termsList = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          number: data.number,
          title: data.title,
          openingDate: data.openingDate ? new Date(data.openingDate) : undefined,
          closingDate: data.closingDate ? new Date(data.closingDate) : undefined,
        } as Term;
      });
      setTerms(termsList);
      if (termsList.length > 0 && !selectedTerm) {
        setSelectedTerm(termsList[0].id);
      }
      
      // Cleanup legacy terms 1, 2, 3 if they exist alongside new terms
      const legacyTerms = termsList.filter(t => ['1', '2', '3'].includes(t.id));
      const hasNewTerms = termsList.some(t => t.id.startsWith('term'));
      
      if (legacyTerms.length > 0 && hasNewTerms) {
        legacyTerms.forEach(async (term) => {
          console.log(`Cleaning up legacy term: ${term.id}`);
          try {
            await deleteDoc(doc(db, `schools/${schoolID}/terms`, term.id));
          } catch (e) {
            console.error(`Failed to delete legacy term ${term.id}`, e);
          }
        });
      }
    }, (error) => {
      console.error("Error fetching terms:", error);
      toast({ title: "Error", description: "Failed to fetch terms.", variant: "destructive" });
    });
    return () => unsubscribe();
  }, [schoolID, isLoading, hasValidClaims, toast]);

  // Create default terms if none exist
  // Create default terms if none exist (using standard IDs now)
  useEffect(() => {
    if (terms.length === 0 && schoolID && hasValidClaims && !isLoading) {
      const currentYear = new Date().getFullYear();
      const defaultTerms = [
        { id: "term1", number: 1, title: `Term 1 ${currentYear}`, openingDate: new Date(currentYear, 0, 15), closingDate: new Date(currentYear, 3, 10) },
        { id: "term2", number: 2, title: `Term 2 ${currentYear}`, openingDate: new Date(currentYear, 4, 1), closingDate: new Date(currentYear, 7, 20) },
        { id: "term3", number: 3, title: `Term 3 ${currentYear}`, openingDate: new Date(currentYear, 8, 5), closingDate: new Date(currentYear, 11, 15) },
      ];
      defaultTerms.forEach(async (term) => {
        await setDoc(doc(db, `schools/${schoolID}/terms`, term.id), {
          number: term.number,
          title: term.title,
          openingDate: format(term.openingDate!, 'yyyy-MM-dd'),
          closingDate: format(term.closingDate!, 'yyyy-MM-dd'),
        });
      });
      // Cleanup legacy terms 1, 2, 3 if they exist
      ['1', '2', '3'].forEach(async (legacyId) => {
        try {
          await deleteDoc(doc(db, `schools/${schoolID}/terms`, legacyId));
        } catch (e) {
          // Ignore if not exists
        }
      });
      setTerms(defaultTerms);
      setSelectedTerm("term1");
    }
  }, [terms.length, schoolID, hasValidClaims, isLoading]);

  // Listen for useRecommendedDates setting
  useEffect(() => {
    if (!schoolID) return;
    const settingsRef = doc(db, `schools/${schoolID}/settings/academics`);
    const unsubscribe = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        setUseRecommendedDates(docSnap.data().useRecommendedDates || false);
      }
    });
    return () => unsubscribe();
  }, [schoolID]);

  // Sync with system dates when enabled
  useEffect(() => {
    const syncDates = async () => {
      // Only run if we have terms loaded to compare against, avoiding blind overwrites
      if (useRecommendedDates && schoolID && terms.length > 0) {
        setLoadingRecommendedDates(true);
        try {
          const configRef = doc(db, "systemConfig/global");
          const configSnap = await getDoc(configRef);
          if (configSnap.exists()) {
            const data = configSnap.data();
            const termDates = data.termDates;
            if (termDates) {
              const currentYear = new Date().getFullYear();
              const parseMMDD = (mmdd: string, year: number) => {
                if (!mmdd) return undefined;
                const [month, day] = mmdd.split('-').map(Number);
                return new Date(year, month - 1, day);
              };
              
              const recommendedSchedule = [
                { id: "term1", start: parseMMDD(termDates.term1Start, currentYear), end: parseMMDD(termDates.term1End, currentYear) },
                { id: "term2", start: parseMMDD(termDates.term2Start, currentYear), end: parseMMDD(termDates.term2End, currentYear) },
                { id: "term3", start: parseMMDD(termDates.term3Start, currentYear), end: parseMMDD(termDates.term3End, currentYear) }
              ];
              
              let updatesMade = false;

              // Check each term and update ONLY if dates are different
              for (const rec of recommendedSchedule) {
                if (!rec.start || !rec.end) continue;
                
                const existingTerm = terms.find(t => t.id === rec.id);
                const recStartStr = format(rec.start, 'yyyy-MM-dd');
                const recEndStr = format(rec.end, 'yyyy-MM-dd');
                
                const currentStartStr = existingTerm?.openingDate ? format(existingTerm.openingDate, 'yyyy-MM-dd') : '';
                const currentEndStr = existingTerm?.closingDate ? format(existingTerm.closingDate, 'yyyy-MM-dd') : '';

                // If term is missing OR dates don't match, we update
                if (!existingTerm || currentStartStr !== recStartStr || currentEndStr !== recEndStr) {
                  // console.log(`[Sync] Updating ${rec.id}: ${currentStartStr} -> ${recStartStr}`);
                  await setDoc(doc(db, `schools/${schoolID}/terms`, rec.id), {
                    openingDate: recStartStr,
                    closingDate: recEndStr,
                  }, { merge: true });
                  updatesMade = true;
                }
              }
              
              if (updatesMade) {
                console.log("Term dates synchronized with recommended schedule.");
                // User requested NO notification, so toast is removed.
              }
            }
          }
        } catch (error) {
          console.error("Error syncing system config:", error);
          // Suppress error toast to avoid nagging
        } finally {
          setLoadingRecommendedDates(false);
        }
      }
    };

    syncDates();
    // Added terms.length to dependency to ensure we have data to compare, 
    // but protected against loops by value checking inside.
  }, [useRecommendedDates, schoolID, terms.length]);

  // Filter events by period

  // Get timetable for specific class (current editing or viewing)
  const getCurrentTimetable = (): Record<string, TimetableEntry> => {
    const classId = timetableMode === "edit" ? currentEditingClass : selectedClassForView;
    if (timetableMode === "edit") {
      return savedTimetables[classId] || {};
    } else {
      return publishedTimetables[classId]?.schedule || {};
    }
  };

  // Get timetable entry for a specific session and day
  const getTimetableEntry = (sessionId: number, day: string): TimetableEntry | null => {
    const currentTimetable = getCurrentTimetable();
    return currentTimetable[`${sessionId}-${day}`] || null;
  };

  // NEW: Enhanced conflict detection functions with proper typing
  const getAllTeacherConflicts = (): TeacherConflict[] => {
    const conflicts: TeacherConflict[] = [];
    const teacherSchedule: Record<string, Record<string, Array<{classId: string, className: string, subjectName: string}>>> = {};

    // Build teacher schedule across all classes
    Object.keys(savedTimetables).forEach(classId => {
      const classTimetable = savedTimetables[classId];
      Object.keys(classTimetable).forEach(key => {
        const [sessionId, day] = key.split('-');
        const entry = classTimetable[key];
        const timeSlot = `${sessionId}-${day}`;
        
        if (!teacherSchedule[entry.teacherId]) {
          teacherSchedule[entry.teacherId] = {};
        }
        
        if (!teacherSchedule[entry.teacherId][timeSlot]) {
          teacherSchedule[entry.teacherId][timeSlot] = [];
        }
        
        teacherSchedule[entry.teacherId][timeSlot].push({
          classId,
          className: classes.find(c => c.id === classId)?.name || 'Unknown Class',
          subjectName: subjects.find(s => s.id === entry.subjectId)?.name || 'Unknown Subject'
        });
      });
    });

    // Find time conflicts (teacher in multiple places at same time)
    Object.keys(teacherSchedule).forEach(teacherIdStr => {
      const teacherId = teacherIdStr;
      const teacher = teachers.find(t => t.id === teacherId);
      Object.keys(teacherSchedule[teacherId]).forEach(timeSlot => {
        const assignments = teacherSchedule[teacherId][timeSlot];
        if (assignments.length > 1) {
          conflicts.push({
            type: 'time',
            teacherId,
            teacherName: teacher?.name || 'Unknown Teacher',
            timeSlot,
            assignments,
            severity: 'high'
          });
        }
      });
    });

    // Load conflicts
    teachers.forEach(teacher => {
      let actual = 0;
      Object.keys(savedTimetables).forEach(classId => {
        const classTimetable = savedTimetables[classId];
        weekdays.forEach(day => {
          sessions.filter(s => !s.isBreak).forEach(s => {
            const key = `${s.id}-${day}`;
            const entry = classTimetable[key];
            if (entry?.teacherId === teacher.id) {
              actual++;
            }
          });
        });
      });
      const assigned = teacherLoads[teacher.id] || 25;
      if (actual > assigned) {
        conflicts.push({
          type: 'load',
          teacherId: teacher.id,
          teacherName: teacher.name,
          timeSlot: `${actual}/${assigned}`,
          assignments: [],
          severity: 'medium'
        });
      }
    });

    return conflicts;
  };

  const getSubjectAnalysis = (): SubjectAnalysis[] => {
    const analysis: Record<string, SubjectAnalysis> = {};
    const currentTimetable = getCurrentTimetable();

    // Count subjects in current class, only periods
    Object.keys(currentTimetable).forEach(key => {
      const entry = currentTimetable[key];
      const [sessionIdStr] = key.split('-');
      const sessionId = parseInt(sessionIdStr);
      const session = sessions.find(s => s.id === sessionId);
      if (session && !session.name.includes('Break') && !session.name.includes('Lunch')) {
        if (!analysis[entry.subjectId]) {
          analysis[entry.subjectId] = {
            subjectId: entry.subjectId,
            subjectName: subjects.find(s => s.id === entry.subjectId)?.name || 'Unknown Subject',
            actual: 0,
            target: subjectTargets[entry.subjectId]?.target || 0
          };
        }
        analysis[entry.subjectId].actual++;
      }
    });

    // Add subjects with targets but no lessons
    Object.keys(subjectTargets).forEach(subjectIdStr => {
      const subjectId = subjectIdStr;
      if (!analysis[subjectId]) {
        analysis[subjectId] = {
          subjectId,
          subjectName: subjects.find(s => s.id === subjectId)?.name || 'Unknown Subject',
          actual: 0,
          target: subjectTargets[subjectId].target
        };
      }
    });

    return Object.values(analysis);
  };

  const getTeacherAvailability = (): Record<string, TeacherAvailability> => {
    const availability: Record<string, TeacherAvailability> = {};

    // Initialize all teachers
    teachers.forEach(teacher => {
      let actual = 0;
      Object.keys(savedTimetables).forEach(classId => {
        const classTimetable = savedTimetables[classId];
        weekdays.forEach(day => {
          sessions.filter(s => !s.isBreak).forEach(s => {
            const key = `${s.id}-${day}`;
            const entry = classTimetable[key];
            if (entry?.teacherId === teacher.id) {
              actual++;
            }
          });
        });
      });
      const assigned = teacherLoads[teacher.id] || 25;
      availability[teacher.id] = {
        name: teacher.name,
        subject: teacher.subject || 'General',
        actualLessons: actual,
        assignedLessons: assigned
      };
    });

    return availability;
  };

  const filteredTeachersForAvailability = useMemo(() => {
    return teachers.filter(teacher => teacher.name.toLowerCase().includes(teacherSearch.toLowerCase()));
  }, [teachers, teacherSearch]);

  // Check for conflicts within the current class timetable
  const getConflicts = (sessionId: number, day: string, teacherId: string, classId: string): string[] => {
    const conflicts: string[] = [];
    
    // Check if teacher is already assigned elsewhere at this time
    Object.keys(savedTimetables).forEach(otherClassId => {
      if (otherClassId !== classId) {
        const otherTimetable = savedTimetables[otherClassId];
        const key = `${sessionId}-${day}`;
        if (otherTimetable[key] && otherTimetable[key].teacherId === teacherId) {
          const otherClass = classes.find(c => c.id === otherClassId);
          conflicts.push(`Teacher already teaching ${otherClass?.name} at this time`);
        }
      }
    });

    return conflicts;
  };

  // Check if cell has conflicts
  const hasConflicts = (sessionId: number, day: string): boolean => {
    const entry = getTimetableEntry(sessionId, day);
    if (!entry) return false;
    
    const conflicts = getConflicts(sessionId, day, entry.teacherId, currentEditingClass);
    return conflicts.length > 0;
  };

  // NEW: Show notification when assignment fails
  const showNotification = (message: string, type: 'error' | 'success' | 'warning' = 'error') => {
    const notification: Notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date().toLocaleTimeString()
    };
    setConflictNotifications(prev => [notification, ...prev.slice(0, 4)]);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      setConflictNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
    
    toast({
      title: type === 'success' ? "Success" : type === 'warning' ? "Warning" : "Error",
      description: message,
      variant: type === 'success' ? "default" : type === 'warning' ? "default" : "destructive",
    });
  };

  // Save subject targets
  const saveSubjectTargets = async () => {
    if (!schoolID) return;
    try {
      await setDoc(doc(db, `schools/${schoolID}/config/subjectTargets`), subjectTargets);
      showNotification('Subject targets saved!', 'success');
    } catch (error) {
      console.error("Save targets error:", error);
      showNotification("Failed to save subject targets.", 'error');
    }
  };

  // Save teacher loads
  const saveTeacherLoads = async () => {
    if (!schoolID) return;
    try {
      await setDoc(doc(db, `schools/${schoolID}/config/teacherLoads`), teacherLoads);
      showNotification('Teacher loads saved!', 'success');
    } catch (error) {
      console.error("Save loads error:", error);
      showNotification("Failed to save teacher loads.", 'error');
    }
  };

  // Assign lesson to timetable
  const assignLesson = (sessionId: number, day: string, assignment: TimetableEntry) => {
    const key = `${sessionId}-${day}`;
    const conflicts = getConflicts(sessionId, day, assignment.teacherId, currentEditingClass);
    
    if (conflicts.length === 0) {
      // Load check
      const teacherId = assignment.teacherId;
      const assignedLoad = teacherLoads[teacherId] || 25;
      let currentLessons = 0;
      const session = sessions.find(s => s.id === sessionId);
      const isPeriodSlot = session && !session.isBreak;
      if (isPeriodSlot) {
        Object.keys(savedTimetables).forEach(classIdStr => {
          const classTimetable = savedTimetables[classIdStr];
          weekdays.forEach(wDay => {
            sessions.filter(s => !s.isBreak).forEach(s => {
              const tKey = `${s.id}-${wDay}`;
              const entry = classTimetable[tKey];
              if (entry?.teacherId === teacherId) {
                currentLessons++;
              }
            });
          });
        });
        const existingEntry = getCurrentTimetable()[key];
        const isCurrentlyThisTeacher = existingEntry?.teacherId === teacherId;
        const delta = isCurrentlyThisTeacher ? 0 : 1;
        const wouldBe = currentLessons + delta;
        if (wouldBe > assignedLoad) {
          showNotification(
            `Warning: Assigning this lesson would give ${assignment.teacherName} ${wouldBe} lessons per week, exceeding their assigned ${assignedLoad} lessons.`,
            'warning'
          );
        }
      }
      
      const currentTimetable = getCurrentTimetable();
      const updatedTimetable = {
        ...currentTimetable,
        [key]: assignment
      };
      
      setSavedTimetables(prev => ({
        ...prev,
        [currentEditingClass]: updatedTimetable
      }));
      
      showNotification('Lesson assigned successfully!', 'success');
      setIsAssignmentOpen(false);
      setSelectedCell(null);
    } else {
      showNotification(`Cannot assign: ${conflicts.join(', ')}`);
    }
  };

  // Remove lesson from timetable
  const removeLesson = (sessionId: number, day: string) => {
    const key = `${sessionId}-${day}`;
    const currentTimetable = getCurrentTimetable();
    const updatedTimetable = { ...currentTimetable };
    delete updatedTimetable[key];
    
    setSavedTimetables(prev => ({
      ...prev,
      [currentEditingClass]: updatedTimetable
    }));
    
    showNotification('Lesson removed successfully!', 'success');
  };

  // Save progress to draft
  const saveProgress = async () => {
    if (!schoolID || !currentEditingClass) return;
    const currentTimetable = getCurrentTimetable();
    try {
      await setDoc(doc(db, `schools/${schoolID}/timetableDrafts`, currentEditingClass), {
        schedule: currentTimetable,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid,
        lastEditedAt: serverTimestamp(),
        editedBy: auth.currentUser?.email || auth.currentUser?.uid || 'Unknown'
      });
      showNotification(`Progress saved for ${classes.find(c => c.id === currentEditingClass)?.name || 'class'}`, 'success');
    } catch (error) {
      console.error("Save progress error:", error);
      showNotification("Failed to save progress.", 'error');
    }
  };

  // Publish timetable
  const publishTimetable = async () => {
    if (!schoolID || !currentEditingClass) return;
    const currentTimetable = getCurrentTimetable();
    
    // First save progress to drafts
    try {
      await setDoc(doc(db, `schools/${schoolID}/timetableDrafts`, currentEditingClass), {
        schedule: currentTimetable,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid,
        lastEditedAt: serverTimestamp(),
        editedBy: auth.currentUser?.email || auth.currentUser?.uid || 'Unknown'
      });
    } catch (saveError) {
      console.error("Save progress error during publish:", saveError);
      // Continue with publish even if save fails
    }
    
    // Then publish
    try {
      await setDoc(doc(db, `schools/${schoolID}/timetablePublished`, currentEditingClass), {
        schedule: currentTimetable,
        publishedAt: serverTimestamp(),
        publishedBy: auth.currentUser?.uid,
        status: 'published'
      });
      showNotification(`Timetable published for ${classes.find(c => c.id === currentEditingClass)?.name || 'class'}`, 'success');
    } catch (error) {
      console.error("Publish error:", error);
      showNotification("Failed to publish timetable.", 'error');
    }
  };

  // Check if timetable is published
  const isTimetablePublished = (classId: string): boolean => {
    return !!publishedTimetables[classId];
  };

  // Filter function for view mode
  const shouldShowCell = (entry: TimetableEntry | null): boolean => {
    if (!entry) return true;
    
    if (filterClass !== "all" && entry.classId !== filterClass) return false;
    if (filterTeacher !== "all" && entry.teacherId !== filterTeacher) return false;
    if (filterSubject !== "all" && entry.subjectId !== filterSubject) return false;
    
    return true;
  };

  // Session management functions
  const addSession = async (sessionData: {name: string, startTime: string, endTime: string, isBreak: boolean}) => {
    if (!schoolID) return;
    const newSessionId = Math.max(...sessions.map(s => s.id)) + 1;
    const newSession = {
      id: newSessionId,
      ...sessionData
    };
    try {
      const collectionName = timetableMode === "edit" ? "sessionsDraft" : "sessionsPublished";
      const sessionsRef = doc(db, `schools/${schoolID}/config/${collectionName}`);
      await updateDoc(sessionsRef, {
        sessions: arrayUnion(newSession)
      });
      setSessions(prev => [...prev, newSession]);
      showNotification('Session added successfully!', 'success');
    } catch (error) {
      console.error("Add session error:", error);
      showNotification("Failed to add session.", 'error');
    }
  };

  const deleteSession = async (sessionId: number) => {
    if (!schoolID) return;
    try {
      const collectionName = timetableMode === "edit" ? "sessionsDraft" : "sessionsPublished";
      const sessionRef = doc(db, `schools/${schoolID}/config/${collectionName}`);
      const sessionDoc = await getDoc(sessionRef);
      if (sessionDoc.exists()) {
        const currentSessions = sessionDoc.data().sessions || [];
        const updatedSessions = currentSessions.filter((s: any) => s.id !== sessionId);
        await updateDoc(sessionRef, { sessions: updatedSessions });
        setSessions(updatedSessions);
        // Remove all timetable entries for this session across all classes (only in edit mode)
        if (timetableMode === "edit") {
          setSavedTimetables(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(classId => {
              const classTimetable = { ...updated[classId] };
              Object.keys(classTimetable).forEach(key => {
                if (key.startsWith(`${sessionId}-`)) {
                  delete classTimetable[key];
                }
              });
              updated[classId] = classTimetable;
            });
            return updated;
          });
        }
        showNotification('Session deleted successfully!', 'success');
      }
    } catch (error) {
      console.error("Delete session error:", error);
      showNotification("Failed to delete session.", 'error');
    }
  };

  const updateSession = async (sessionData: {id: number, name: string, startTime: string, endTime: string, isBreak: boolean}) => {
    if (!schoolID) return;
    try {
      const collectionName = timetableMode === "edit" ? "sessionsDraft" : "sessionsPublished";
      const sessionRef = doc(db, `schools/${schoolID}/config/${collectionName}`);
      const sessionDoc = await getDoc(sessionRef);
      if (sessionDoc.exists()) {
        const currentSessions = sessionDoc.data().sessions || [];
        const updatedSessions = currentSessions.map((s: any) => 
          s.id === sessionData.id ? sessionData : s
        );
        await updateDoc(sessionRef, { sessions: updatedSessions });
        setSessions(updatedSessions);
        setEditingSession(null);
        showNotification('Session updated successfully!', 'success');
      }
    } catch (error) {
      console.error("Update session error:", error);
      showNotification("Failed to update session.", 'error');
    }
  };

  // Add event

  // Update event
  const updateEvent = async (event: CalendarEvent) => {
    if (!schoolID) return;
    try {
      await updateDoc(doc(db, `schools/${schoolID}/events`, event.id), {
        title: event.title,
        type: event.type,
        date: format(event.date, 'yyyy-MM-dd'),
        description: event.description,
      });
      showNotification("Event updated successfully!", 'success');
    } catch (error) {
      console.error("Update event error:", error);
      showNotification("Failed to update event.", 'error');
    }
  };

  // Delete event
  const deleteEvent = async (id: string) => {
    if (!schoolID) return;
    try {
      await deleteDoc(doc(db, `schools/${schoolID}/events`, id));
      showNotification("Event deleted successfully!", 'success');
    } catch (error) {
      console.error("Delete event error:", error);
      showNotification("Failed to delete event.", 'error');
    }
  };

  // Save term configuration
  const saveTermConfiguration = async () => {
    if (!schoolID || !selectedTerm) return;
    const term = terms.find(t => t.id === selectedTerm);
    if (!term) return;
    try {
      await updateDoc(doc(db, `schools/${schoolID}/terms`, selectedTerm), {
        title: term.title,
        openingDate: format(term.openingDate!, 'yyyy-MM-dd'),
        closingDate: format(term.closingDate!, 'yyyy-MM-dd'),
        number: term.number,
      });
      showNotification('Term configuration saved!', 'success');
    } catch (error) {
      console.error("Save term error:", error);
      showNotification("Failed to save term configuration.", 'error');
    }
  };

  // Handle save event
  const handleSaveEvent = async () => {
    if (!eventForm.title || !eventForm.date || !eventForm.description) {
      showNotification("Please fill all fields.", 'error');
      return;
    }
    if (editingEvent) {
      const updatedEvent = {
        ...editingEvent,
        title: eventForm.title,
        type: eventForm.type || editingEvent.type,
        date: typeof eventForm.date === "string" ? eventForm.date : format(eventForm.date!, "yyyy-MM-dd"),
        description: eventForm.description,
      };
      await updateEvent(updatedEvent as CalendarEvent);
      setCalendarEvents(prev => prev.map(e => 
        e.id === editingEvent.id 
          ? { ...updatedEvent, date: typeof updatedEvent.date === "string" ? updatedEvent.date : format(updatedEvent.date, "yyyy-MM-dd") }
          : e
      ));
      setEditingEvent(null);
    } else {
      const newEventData = {
        title: eventForm.title,
        type: eventForm.type,
        date: format(eventForm.date!, 'yyyy-MM-dd'),
        description: eventForm.description,
        createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, `schools/${schoolID}/events`), newEventData);
      const newEvent = { id: docRef.id, ...newEventData, date: format(eventForm.date!, "yyyy-MM-dd") };
      setCalendarEvents(prev => [...prev, newEvent]);
    }
    setIsEventDialogOpen(false);
    setEventForm({ title: "", type: "", date: undefined, description: "" });
  };

  const academicStats = [
    {
      icon: BookOpen,
      title: "Active Courses",
      value: "156",
      description: "Across all grade levels",
      color: "bg-purple-500"
    },
    {
      icon: GraduationCap,
      title: "Average GPA",
      value: "3.7",
      description: "School-wide performance",
      color: "bg-blue-500"
    },
    {
      icon: Award,
      title: "Honor Students",
      value: "89",
      description: "GPA above 3.5",
      color: "bg-yellow-500"
    },
    {
      icon: TrendingUp,
      title: "Pass Rate",
      value: "94.2%",
      description: "This academic year",
      color: "bg-green-500"
    }
  ];


  const performanceData = [
    { subject: "Mathematics", average: 85, students: 245 },
    { subject: "English", average: 78, students: 267 },
    { subject: "Science", average: 82, students: 223 },
    { subject: "History", average: 76, students: 189 },
    { subject: "PE", average: 90, students: 298 },
  ];

  const teacherPerformance = [
    { name: "Dr. Smith", subject: "Mathematics", rating: 4.8, students: 95, icon: GraduationCap, color: "bg-blue-500" },
    { name: "Ms. Johnson", subject: "English", rating: 4.6, students: 87, icon: BookOpen, color: "bg-emerald-500" },
    { name: "Mr. Brown", subject: "Science", rating: 4.7, students: 78, icon: Zap, color: "bg-amber-500" },
  ];


  const monthlyProgress = [
    { month: "Jan", performance: 82 },
    { month: "Feb", performance: 85 },
    { month: "Mar", performance: 88 },
    { month: "Apr", performance: 87 },
    { month: "May", performance: 90 },
    { month: "Jun", performance: 92 },
  ];

  if (isLoading) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <Adminsidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <Adminheader />
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (networkError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <p className="text-lg text-red-600">Network error occurred. Please check your internet connection and try again.</p>
        <Button
          onClick={() => {
            setIsLoading(true);
            setNetworkError(false);
            auth.currentUser?.getIdToken(true);
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!hasValidClaims) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-lg text-red-600">No valid permissions to view academics management.</p>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <div className="hidden md:block md:fixed md:top-0 md:left-0 md:h-screen md:w-64 md:z-50">
          <Adminsidebar onSectionChange={() => {}} />
        </div>
        <div className="flex flex-col flex-1 h-full md:ml-64">
          <Adminheader />
          <main className="flex-1 overflow-y-auto p-3 sm:p-6 bg-[#FFFFFF] dark:bg-[#020817]">
            <TooltipProvider>
              <div className="mb-4 sm:mb-6">
                <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">Academic Management</h1>
                <p className="text-sm sm:text-base text-muted-foreground">Comprehensive academic system management</p>
              </div>

              {showReportPreview ? (
                <div className="w-full h-full overflow-auto">
                  <style>
                    {`
                      @media print {
                        body * {
                          visibility: hidden;
                        }
                        #printable-report, #printable-report * {
                          visibility: visible;
                        }
                        #printable-report {
                          position: absolute;
                          left: 0;
                          top: 0;
                          width: 100%;
                        }
                      }
                    `}
                  </style>
                  <div className="flex flex-col md:flex-row items-center justify-between mb-4 gap-4">
                    <div className="flex items-center gap-4 w-full md:w-auto">
                        <Button variant="outline" onClick={() => setShowReportPreview(false)} className="gap-2">
                        <ArrowLeft className="h-4 w-4" /> Back to Grading
                        </Button>
                        <h2 className="text-xl font-bold hidden sm:block">Report Preview</h2>
                    </div>
                    
                    <div className="flex items-center gap-2 w-full md:w-auto bg-white dark:bg-[#020817] p-2 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm">
                        <Label className="whitespace-nowrap hidden sm:block">Template:</Label>
                        <Select value={selectedTemplate} onValueChange={(val) => setSelectedTemplate(val as ReportTemplate)}>
                            <SelectTrigger className="w-full md:w-[240px]">
                                <SelectValue placeholder="Select template" />
                            </SelectTrigger>
                            <SelectContent>
                                {TEMPLATES.map(t => (
                                    <SelectItem key={t.id} value={t.id}>
                                        <span className="font-medium">{t.name}</span>
                                        <span className="text-xs text-muted-foreground ml-2 hidden sm:inline"> - {t.description}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button onClick={() => setIsApplyThemeDialogOpen(true)} variant="outline" size="sm" className="ml-2 whitespace-nowrap border-blue-200 hover:bg-blue-50 text-blue-700 dark:border-blue-800 dark:hover:bg-blue-950 dark:text-blue-300">
                            <CheckCircle className="w-4 h-4 mr-2" /> Apply Style
                        </Button>
                        <Button onClick={() => window.print()} variant="default" size="sm" className="ml-2 whitespace-nowrap">
                            <Download className="w-4 h-4 mr-2" /> Print
                        </Button>
                    </div>
                  </div>
                  <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-8 bg-slate-50/50 dark:bg-slate-900/50 min-h-[600px] flex justify-center overflow-auto shadow-inner">
                    <ReportPreview data={SAMPLE_DATA} template={selectedTemplate} />
                  </div>

                  <Dialog open={isApplyThemeDialogOpen} onOpenChange={setIsApplyThemeDialogOpen}>
                    <DialogContent className="sm:max-w-md border border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm shadow-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-center text-xl">Apply Theme</DialogTitle>
                        <DialogDescription className="text-center pt-2">
                          Are you sure you want to apply <span className="font-bold text-foreground">{TEMPLATES.find(t => t.id === selectedTemplate)?.name}</span> to schools report forms?
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter className="sm:justify-center gap-2 mt-4">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => setIsApplyThemeDialogOpen(false)}
                          className="w-24"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          className="w-24 bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => {
                            toast({
                              title: "Theme Applied",
                              description: `Report theme updated to ${TEMPLATES.find(t => t.id === selectedTemplate)?.name}`,
                            });
                            setIsApplyThemeDialogOpen(false);
                          }}
                        >
                          Apply
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid grid-cols-2 grid-rows-3 sm:grid-rows-2 lg:grid-rows-1 sm:grid-cols-3 lg:grid-cols-5 w-full mb-4 sm:mb-6 h-auto border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] p-1 gap-1">
                  <TabsTrigger value="overview" className="text-[11px] sm:text-xs lg:text-sm px-1 py-2 data-[state=active]:!bg-[#3879E3] data-[state=active]:!text-white rounded">Overview</TabsTrigger>
                  <TabsTrigger value="timetable" className="text-[11px] sm:text-xs lg:text-sm px-1 py-2 data-[state=active]:!bg-[#3879E3] data-[state=active]:!text-white rounded">Timetable</TabsTrigger>
                  <TabsTrigger value="calendar" className="text-[11px] sm:text-xs lg:text-sm px-1 py-2 data-[state=active]:!bg-[#3879E3] data-[state=active]:!text-white rounded">Calendar</TabsTrigger>
                  <TabsTrigger value="grading" className="text-[11px] sm:text-xs lg:text-sm px-1 py-2 data-[state=active]:!bg-[#3879E3] data-[state=active]:!text-white rounded">Grading</TabsTrigger>
                  <TabsTrigger value="analytics" className="text-[11px] sm:text-xs lg:text-sm px-1 py-2 data-[state=active]:!bg-[#3879E3] data-[state=active]:!text-white rounded col-span-2 sm:col-span-1">Analytics</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {academicStats.map((stat, index) => (
                      <Card key={index} className="group relative overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                        <div className={`absolute top-0 left-0 w-1 h-full ${stat.color} opacity-80 group-hover:w-1.5 transition-all duration-300`}></div>
                        <CardHeader className="p-4 md:pb-2">
                          <div className="flex items-center justify-between">
                            <div className={`w-10 h-10 md:w-12 md:h-12 ${stat.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                              <stat.icon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">{stat.title.split(' ')[0]}</div>
                          </div>
                          <CardTitle className="text-xl md:text-3xl font-black text-foreground mt-4 tracking-tight">
                            {stat.value}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="px-4 pb-4 pt-0">
                          <h3 className="font-bold text-foreground text-sm uppercase tracking-wide group-hover:text-primary transition-colors">{stat.title}</h3>
                          <CardDescription className="text-muted-foreground text-[10px] md:text-xs mt-1 leading-relaxed">
                            {stat.description}
                          </CardDescription>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                          <BookOpen className="h-4 w-4 sm:h-5 sm:w-5" />
                          Course Performance
                        </CardTitle>
                        <CardDescription className="text-xs sm:text-sm">Average grades by subject</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-1.5">
                          {[
                            { subject: "Mathematics", average: 3.8, students: 245, grade: "A-", icon: BarChart3, color: "text-blue-500" },
                            { subject: "English", average: 3.6, students: 267, grade: "B+", icon: BookOpen, color: "text-emerald-500" },
                            { subject: "Science", average: 3.7, students: 223, grade: "A-", icon: Zap, color: "text-amber-500" },
                            { subject: "History", average: 3.5, students: 189, grade: "B+", icon: FileText, color: "text-rose-500" },
                            { subject: "Physical Education", average: 3.9, students: 298, grade: "A", icon: Target, color: "text-indigo-500" }
                          ].map((course, index) => (
                            <div key={index} className="group flex items-center justify-between p-2.5 rounded-xl border border-slate-100 dark:border-slate-800/40 hover:border-blue-500/20 bg-gray-50/50 dark:bg-white/[0.02] hover:bg-white dark:hover:bg-white/[0.04] transition-all duration-300">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg bg-slate-100 dark:bg-slate-800 ${course.color} group-hover:scale-110 transition-transform`}>
                                  <course.icon className="h-4 w-4" />
                                </div>
                                <div className="space-y-0.5">
                                  <p className="font-bold text-foreground text-sm leading-none">{course.subject}</p>
                                  <div className="flex items-center gap-1.5 opacity-60">
                                     <Users className="h-3 w-3" />
                                     <span className="text-[10px] font-medium">{course.students} students</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <div className="flex items-center gap-2">
                                     <span className={`text-xs font-black uppercase ${course.grade.startsWith('A') ? 'text-green-500' : 'text-blue-500'}`}>{course.grade}</span>
                                     <div className="h-3 w-[1px] bg-slate-200 dark:bg-slate-800" />
                                     <span className="text-[10px] font-bold text-muted-foreground">{course.average.toFixed(1)}</span>
                                  </div>
                                  <div className="w-16 h-1 bg-slate-100 dark:bg-slate-800 rounded-full mt-1 overflow-hidden">
                                     <div className={`h-full ${course.grade.startsWith('A') ? 'bg-green-500' : 'bg-blue-500'} rounded-full`} style={{ width: `${(course.average/4)*100}%` }} />
                                  </div>
                                </div>
                                <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-all">
                                   <Eye className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <Button variant="ghost" className="w-full mt-4 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors">
                          Detailed Performance <TrendingUp className="h-3 w-3 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>

                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                          <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          Upcoming Events
                        </CardTitle>
                        <CardDescription className="text-xs sm:text-sm">Academic calendar and important dates</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-1.5">
                        <div className="space-y-1.5">
                          {calendarEvents
                            .filter(event => {
                              if (!event.date) return false;
                              const eventDate = new Date(event.date);
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              return eventDate >= today;
                            })
                            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                            .slice(0, 5)
                            .map((item, index) => {
                              const eventDate = new Date(item.date);
                              return (
                            <div key={index} className="group flex items-center gap-4 p-2.5 rounded-xl border border-slate-100 dark:border-slate-800/40 hover:border-purple-500/20 bg-gray-50/50 dark:bg-white/[0.02] hover:bg-white dark:hover:bg-white/[0.04] transition-all duration-300">
                              <div className={`w-11 h-11 rounded-2xl bg-blue-500 flex flex-col items-center justify-center text-white shadow-lg group-hover:scale-110 transition-all duration-300`}>
                                <CalendarIcon className="h-5 w-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-bold text-foreground text-sm truncate leading-none">{item.title}</p>
                                  <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full text-blue-500 bg-white dark:bg-white/5 border border-current/20`}>{item.type.split(' ')[0]}</span>
                                </div>
                                <div className="flex items-center gap-3 mt-1.5">
                                   <div className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
                                      <CalendarIcon className="h-3 w-3" />
                                      {format(eventDate, "MMM do, yyyy")}
                                   </div>
                                </div>
                              </div>
                            </div>
                          )})}
                        </div>
                        </div>
                        <Button variant="ghost" className="w-full mt-4 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors">
                          Academic Calendar <CalendarIcon className="h-3 w-3 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="timetable" className="space-y-4 sm:space-y-6 relative">
                  {/* Enhanced Timetable Management with View/Edit Modes */}
                  <div className="flex flex-col gap-3 sm:gap-4">
                    <div>
                      <h2 className="text-lg sm:text-xl font-semibold text-foreground">School Timetable System</h2>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {timetableMode === "view" ? "View class timetables" : "Create and edit timetables with conflict detection"}
                      </p>
                    </div>
                    
                    <div className="flex flex-col gap-2 w-full">
                      {/* Mode Toggle */}
                      <div className="flex rounded-lg border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] p-1 w-full sm:w-fit">
                        <Button
                          variant={timetableMode === "view" ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setTimetableMode("view")}
                          className={`flex-1 sm:flex-none text-xs ${timetableMode === "view" ? "bg-[#3879E3] hover:bg-[#3879E3]/90 text-white" : ""}`}
                        >
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          <span className="hidden xs:inline">View </span>Mode
                        </Button>
                        <Button
                          variant={timetableMode === "edit" ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setTimetableMode("edit")}
                          className={`flex-1 sm:flex-none text-xs ${timetableMode === "edit" ? "bg-[#3879E3] hover:bg-[#3879E3]/90 text-white" : ""}`}
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          <span className="hidden xs:inline">Edit </span>Mode
                        </Button>
                      </div>

                      {timetableMode === "edit" && (
                        <div className="flex flex-col sm:flex-row gap-2 w-full">
                          <Dialog open={isSessionManagerOpen} onOpenChange={setIsSessionManagerOpen}>
                            <DialogTrigger asChild>
                              <Button variant="outline" className="flex-1 sm:flex-none text-xs border border-[#E2E8F0] dark:border-[#1E293B]">
                                <Settings className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                Sessions
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                              <DialogHeader>
                                <DialogTitle>Session Manager</DialogTitle>
                                <DialogDescription>Define and manage time slots for your timetable</DialogDescription>
                              </DialogHeader>
                              
                              <div className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg bg-[#FFFFFF] dark:bg-[#020817]">
                                  <div>
                                    <Label htmlFor="session-name">Session Name</Label>
                                    <Input 
                                      id="session-name" 
                                      placeholder="e.g., Period 1"
                                      value={newSession.name}
                                      onChange={(e) => setNewSession(prev => ({ ...prev, name: e.target.value }))}
                                      className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="start-time">Start Time</Label>
                                    <Input 
                                      id="start-time" 
                                      type="time"
                                      value={newSession.startTime}
                                      onChange={(e) => setNewSession(prev => ({ ...prev, startTime: e.target.value }))}
                                      className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="end-time">End Time</Label>
                                    <Input 
                                      id="end-time" 
                                      type="time"
                                      value={newSession.endTime}
                                      onChange={(e) => setNewSession(prev => ({ ...prev, endTime: e.target.value }))}
                                      className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                                    />
                                  </div>
                                  <div className="flex items-center space-x-2 sm:col-span-3">
                                    <Checkbox id="is-break" checked={newSession.isBreak} onCheckedChange={(checked) => setNewSession(prev => ({ ...prev, isBreak: Boolean(checked) }))} />
                                    <Label htmlFor="is-break" className="ml-2">Break</Label>
                                  </div>
                                  <Button 
                                    className="sm:col-span-3"
                                    onClick={() => {
                                      if (newSession.name && newSession.startTime && newSession.endTime) {
                                        addSession(newSession);
                                        setNewSession({ name: '', startTime: '', endTime: '', isBreak: false });
                                      }
                                    }}
                                  >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add Session
                                  </Button>
                                </div>

                                <div className="space-y-2">
                                  <h4 className="font-medium">Current Sessions</h4>
                                  {sessions.map((session) => (
                                    <div key={session.id} className="border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg bg-[#FFFFFF] dark:bg-[#020817] m-2">
                                      {editingSession && editingSession.id === session.id ? (
                                        <div className="p-3 space-y-3">
                                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <div>
                                              <Label htmlFor={`edit-session-name-${session.id}`} className="text-xs">Session Name</Label>
                                              <Input
                                                id={`edit-session-name-${session.id}`}
                                                value={editingSession.name}
                                                onChange={(e) => setEditingSession({...editingSession, name: e.target.value})}
                                                className="h-8 text-xs border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                                              />
                                            </div>
                                            <div>
                                              <Label htmlFor={`edit-start-time-${session.id}`} className="text-xs">Start Time</Label>
                                              <Input
                                                id={`edit-start-time-${session.id}`}
                                                type="time"
                                                value={editingSession.startTime}
                                                onChange={(e) => setEditingSession({ ...editingSession, startTime: e.target.value })}
                                                className="h-8 text-xs border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                                              />
                                            </div>
                                            <div>
                                              <Label htmlFor={`edit-end-time-${session.id}`} className="text-xs">End Time</Label>
                                              <Input
                                                id={`edit-end-time-${session.id}`}
                                                type="time"
                                                value={editingSession.endTime}
                                                onChange={(e) => setEditingSession({...editingSession, endTime: e.target.value})}
                                                className="h-8 text-xs border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                                              />
                                            </div>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            <Checkbox
                                              id={`edit-is-break-${session.id}`}
                                              checked={editingSession.isBreak}
                                              onCheckedChange={(checked) => setEditingSession({...editingSession, isBreak: Boolean(checked)})}
                                            />
                                            <Label htmlFor={`edit-is-break-${session.id}`} className="text-xs ml-2">Break</Label>
                                          </div>
                                          <div className="flex gap-2">
                                            <Button
                                              size="sm"
                                              onClick={() => updateSession(editingSession)}
                                              className="flex-1 h-8 text-xs"
                                            >
                                              <Check className="w-3 h-3 mr-1" />
                                              Save
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => setEditingSession(null)}
                                              className="flex-1 h-8 text-xs border border-[#E2E8F0] dark:border-[#1E293B]"
                                            >
                                              <X className="w-3 h-3 mr-1" />
                                              Cancel
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex items-center justify-between p-3">
                                          <div>
                                            <p className="font-medium flex items-center gap-2">{session.name} {session.isBreak && <Check className="w-4 h-4 text-green-500" />}</p>
                                            <p className="text-sm text-muted-foreground">{session.startTime} - {session.endTime}</p>
                                          </div>
                                          <div className="flex gap-2">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => setEditingSession(session)}
                                              className="border border-[#E2E8F0] dark:border-[#1E293B] h-8 w-8 p-0"
                                            >
                                              <Edit className="w-3 h-3" />
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => deleteSession(session.id)}
                                              className="border border-[#E2E8F0] dark:border-[#1E293B] h-8 w-8 p-0"
                                            >
                                              <Trash2 className="w-3 h-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                          <Button
                            variant="outline"
                            className="flex-1 sm:flex-none text-xs border border-[#E2E8F0] dark:border-[#1E293B]"
                            onClick={() => setIsConflictAnalysisOpen(true)}
                          >
                            <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            Analysis
                          </Button>

                          <Button onClick={saveProgress} variant="outline" className="flex-1 sm:flex-none text-xs border border-[#E2E8F0] dark:border-[#1E293B]">
                            <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            Save Progress
                          </Button>

                          <Button onClick={publishTimetable} className="flex-1 sm:flex-none text-xs bg-[#3879E3] hover:bg-[#3879E3]/90 text-white">
                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            Publish
                          </Button>
                        </div>
                      )}

                      <Button className="w-full sm:w-auto text-xs bg-[#3879E3] hover:bg-[#3879E3]/90 text-white mb-2">
                        <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        Export
                      </Button>
                    </div>
                  </div>

                  {/* NEW: Conflict Notifications - Only in Edit Mode */}
                  {timetableMode === "edit" && conflictNotifications.length > 0 && (
                    <div className="space-y-2">
                      {conflictNotifications.map((notification) => (
                        <Alert key={notification.id} className={`border border-[#E2E8F0] dark:border-[#1E293B] ${notification.type === 'success' ? 'border-green-500 bg-green-50 dark:bg-green-950' : 'border-red-500 bg-red-50 dark:bg-red-950'}`}>
                          {notification.type === 'success' ? <Check className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                          <AlertDescription className="flex justify-between items-center">
                            <span>{notification.message}</span>
                            <span className="text-xs text-muted-foreground">{notification.timestamp}</span>
                          </AlertDescription>
                        </Alert>
                      ))}
                    </div>
                  )}

                  {/* Class Selection and Mode-specific Controls */}
                  <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                    <CardHeader className="p-4">
                      <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                        {timetableMode === "view" ? <Eye className="h-4 w-4 sm:h-5 sm:w-5" /> : <Edit className="h-4 w-4 sm:h-5 sm:w-5" />}
                        {timetableMode === "view" ? "View Timetable" : "Edit Timetable"}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="grid grid-cols-1 gap-3 sm:gap-4">
                        <div>
                          <Label htmlFor="class-select" className="text-xs sm:text-sm">
                            {timetableMode === "view" ? "Select Class to View" : "Select Class to Edit"}
                          </Label>
                          <Select 
                            value={timetableMode === "view" ? selectedClassForView : currentEditingClass} 
                            onValueChange={timetableMode === "view" ? setSelectedClassForView : setCurrentEditingClass}
                          >
                            <SelectTrigger className="w-full border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                              <SelectValue placeholder="Select Class" />
                            </SelectTrigger>
                            <SelectContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] dark:[&_[data-state=hovered]]:bg-[#1E293B]">
                              {classes.map((cls) => (
                                <SelectItem key={cls.id} value={cls.id}>
                                  <div className="flex flex-col gap-1 py-1">
                                    <div className="flex items-center justify-between w-full">
                                      <span className="font-medium">{cls.name}</span>
                                      <div className="flex items-center gap-1">
                                        {isTimetablePublished(cls.id) && (
                                          <Badge variant="secondary" className="text-xs">Published</Badge>
                                        )}
                                        {timetableMode === "edit" && lastEditedInfo[cls.id] && (
                                          <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300">
                                            Last Edited
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    {timetableMode === "edit" && lastEditedInfo[cls.id] && (
                                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                        <Clock className="w-3 h-3" />
                                        <span>
                                          {new Date(lastEditedInfo[cls.id].lastEditedAt?.toDate?.() || lastEditedInfo[cls.id].lastEditedAt).toLocaleString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {timetableMode === "view" && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <Label htmlFor="filter-teacher" className="text-xs sm:text-sm">Filter by Teacher</Label>
                              <Select value={filterTeacher} onValueChange={setFilterTeacher}>
                                <SelectTrigger className="w-full border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                                  <SelectValue placeholder="All Teachers" />
                                </SelectTrigger>
                                <SelectContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] dark:[&_[data-state=hovered]]:bg-[#1E293B]">
                                  <SelectItem value="all">All Teachers</SelectItem>
                                  {teachers.map((teacher) => (
                                    <SelectItem key={teacher.id} value={teacher.id}>{teacher.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <Label htmlFor="filter-subject" className="text-xs sm:text-sm">Filter by Subject</Label>
                              <Select value={filterSubject} onValueChange={setFilterSubject}>
                                <SelectTrigger className="w-full border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                                  <SelectValue placeholder="All Subjects" />
                                </SelectTrigger>
                                <SelectContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] dark:[&_[data-state=hovered]]:bg-[#1E293B]">
                                  <SelectItem value="all">All Subjects</SelectItem>
                                  {subjects.map((subject) => (
                                    <SelectItem key={subject.id} value={subject.id}>{subject.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}

                        {timetableMode === "edit" && (
                          <div className="w-full">
                            <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground p-2 bg-muted/50 rounded-lg">
                              <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                              <span>Tap time slots to assign lessons. Red highlights show conflicts.</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Status Alert for Published Timetables */}
                  {isTimetablePublished(timetableMode === "view" ? selectedClassForView : currentEditingClass) && (
                    <Alert className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CheckCircle className="h-4 w-4" />
                      <AlertDescription>
                        This timetable has been published and is visible to teachers and students.
                        {timetableMode === "edit" && " Any changes will require republishing."}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Timetable Grid */}
                  <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                    <CardHeader className="p-4">
                      <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                        <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
                        Weekly Timetable - {classes.find(c => c.id === (timetableMode === "view" ? selectedClassForView : currentEditingClass))?.name}
                      </CardTitle>
                      <CardDescription>
                        {timetableMode === "view" 
                          ? "View the scheduled lessons for this class" 
                          : "Click on any cell to assign lessons. Red highlights indicate conflicts."
                        }
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-2 sm:p-6">
                      {/* Mobile Card Layout for Both Modes */}
                      <div className="md:hidden space-y-3">
                        <div className="text-xs text-muted-foreground text-center p-2 bg-muted rounded">
                          {timetableMode === "edit" ? "Mobile Timetable Editor - Tap sessions to edit" : "Mobile Timetable View"}
                        </div>
                        {sessions.map((session) => {
                          // Render break sessions as a simple banner
                          if (session.isBreak || session.name.toLowerCase().includes('break') || session.name.toLowerCase().includes('lunch')) {
                            return (
                              <div key={session.id} className="w-full p-3 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                    <Clock className="w-4 h-4" />
                                    <span>{session.name}</span>
                                    <span className="text-xs">({session.startTime} - {session.endTime})</span>
                                  </div>
                                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                </div>
                              </div>
                            );
                          }

                          return (
                          <Card key={session.id} className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                            <CardHeader className="pb-2 p-4">
                              <CardTitle className="text-sm flex justify-between items-center">
                                <span>{session.name}</span>
                                <span className="text-xs text-muted-foreground font-normal">
                                  {session.startTime} - {session.endTime}
                                </span>
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0 p-4">
                              <div className="grid grid-cols-1 gap-2">
                                {weekdays.map((day) => {
                                  const entry = getTimetableEntry(session.id, day);
                                  const hasConflict = timetableMode === "edit" && hasConflicts(session.id, day);
                                  const shouldShow = timetableMode === "view" ? shouldShowCell(entry) : true;
                                  
                                  if (timetableMode === "view" && !shouldShow) return null;
                                  
                                  return (
                                    <div key={day} className={`flex justify-between items-center p-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] rounded hover:bg-gray-50 dark:hover:bg-[#101929] m-1 ${hasConflict ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
                                      <div className="flex flex-col">
                                        <span className="text-xs font-medium text-muted-foreground">{day}</span>
                                        {entry ? (
                                          <div className={`${hasConflict ? 'text-red-600' : 'text-foreground'}`}>
                                            <div className="text-sm font-medium">
                                              {subjects.find(s => s.id === entry.subjectId)?.name}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                              {teachers.find(t => t.id === entry.teacherId)?.name}
                                            </div>
                                            {hasConflict && (
                                              <div className="flex items-center gap-1 text-xs text-red-600">
                                                <AlertTriangle className="w-3 h-3" />
                                                Conflict
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-xs text-muted-foreground">No lesson</span>
                                        )}
                                      </div>
                                      {timetableMode === "edit" && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            if (!currentEditingClass) {
                                              toast({
                                                title: 'No class selected',
                                                description: 'Please select a class before assigning a lesson.',
                                                variant: 'destructive',
                                              });
                                              return;
                                            }
                                            setSelectedCell({ sessionId: session.id, day });
                                            setIsAssignmentOpen(true);
                                          }}
                                          className={`h-8 w-8 p-0 ${hasConflict ? 'border-red-300 bg-red-50' : ''}`}
                                        >
                                          {entry ? <Edit className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                        </Button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </CardContent>
                          </Card>
                          );
                        })}
                      </div>

                      {/* Desktop Grid Layout */}
                      <div className="hidden md:block overflow-x-auto">
                        <div className="min-w-[700px]">
                          {/* Header Row */}
                          <div className="grid gap-2 mb-4 grid-cols-[80px_1fr_1fr_1fr_1fr_1fr] lg:grid-cols-[100px_1fr_1fr_1fr_1fr_1fr]">
                            <div className="font-semibold p-2 bg-[#F1F5F9] dark:bg-[#1E293B] border border-[#E2E8F0] dark:border-[#1E293B] rounded text-center text-xs">
                              Time
                            </div>
                            {weekdays.map((day) => (
                              <div key={day} className="font-semibold p-2 bg-[#F1F5F9] dark:bg-[#1E293B] border border-[#E2E8F0] dark:border-[#1E293B] rounded text-center text-xs">
                                <span className="hidden lg:inline">{day}</span>
                                <span className="lg:hidden">{day.slice(0, 3)}</span>
                              </div>
                            ))}
                          </div>

                          {/* Timetable Rows */}
                          {sessions.map((session) => {
                            // Render break sessions as a full-width banner
                            if (session.isBreak || session.name.toLowerCase().includes('break') || session.name.toLowerCase().includes('lunch')) {
                              return (
                                <div key={session.id} className="w-full mb-4 p-3 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg">
                                  <div className="flex items-center justify-center gap-3">
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                      <Clock className="w-4 h-4" />
                                      <span>{session.name}</span>
                                      <span className="text-xs">({session.startTime} - {session.endTime})</span>
                                    </div>
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                                  </div>
                                </div>
                              );
                            }

                            return (
                            <div key={session.id} className="grid gap-2 mb-4 grid-cols-[80px_1fr_1fr_1fr_1fr_1fr] lg:grid-cols-[100px_1fr_1fr_1fr_1fr_1fr]">
                              {/* Session Time Column */}
                              <div className="p-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#F1F5F9] dark:bg-[#1E293B] rounded font-medium text-center flex flex-col justify-center text-xs min-h-[60px]">
                                <div className="font-semibold leading-tight">
                                  {session.name.replace("Period ", "P")}
                                </div>
                                <div className="text-[10px] text-muted-foreground leading-tight">
                                  {session.startTime}-{session.endTime}
                                </div>
                              </div>

                              {/* Day Columns */}
                              {weekdays.map((day) => {
                                const entry = getTimetableEntry(session.id, day);
                                const hasConflict = timetableMode === "edit" && hasConflicts(session.id, day);
                                const shouldShow = timetableMode === "view" ? shouldShowCell(entry) : true;

                                return (
                                  <Tooltip key={day}>
                                    <TooltipTrigger asChild>
                                      <div
                                        className={`
                                          p-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] rounded transition-all duration-200 flex flex-col justify-center
                                          ${timetableMode === "edit" ? "min-h-[60px] cursor-pointer" : "min-h-[60px] cursor-default"}
                                          ${entry ? 'bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800' : timetableMode === "edit" ? 'hover:bg-gray-50 dark:hover:bg-[#101929]' : ''}
                                          ${hasConflict ? 'bg-red-50 border-red-300 dark:bg-red-950 dark:border-red-800' : ''}
                                          ${!shouldShow ? 'opacity-30' : ''}
                                          text-xs
                                        `}
                                        onClick={() => {
                                          if (timetableMode === "edit") {
                                            if (!currentEditingClass) {
                                              toast({
                                                title: 'No class selected',
                                                description: 'Please select a class before assigning a lesson.',
                                                variant: 'destructive',
                                              });
                                              return;
                                            }
                                            setSelectedCell({ sessionId: session.id, day });
                                            setIsAssignmentOpen(true);
                                          }
                                        }}
                                      >
                                        {entry && shouldShow && (
                                          <div className="space-y-1">
                                            <div className="font-medium text-foreground leading-tight text-center break-words">
                                              {subjects.find(s => s.id === entry.subjectId)?.name}
                                            </div>
                                            <div className="text-xs text-muted-foreground leading-tight text-center break-words">
                                              {teachers.find(t => t.id === entry.teacherId)?.name}
                                            </div>
                                            {hasConflict && (
                                              <div className="flex items-center justify-center text-red-600 dark:text-red-400">
                                                <AlertTriangle className="w-3 h-3" />
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        {!entry && timetableMode === "edit" && (
                                          <div className="flex items-center justify-center h-full text-muted-foreground">
                                            <Plus className="w-4 h-4" />
                                          </div>
                                        )}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs whitespace-normal">
                                      <p className="break-words">
                                        {entry 
                                          ? `${subjects.find(s => s.id === entry.subjectId)?.name} - ${teachers.find(t => t.id === entry.teacherId)?.name}`
                                          : timetableMode === "edit" ? 'Click to assign lesson' : 'No lesson scheduled'
                                        }
                                      </p>
                                      {hasConflict && (
                                        <p className="text-red-400 mt-1">
                                          Scheduling conflict detected!
                                        </p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              })}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Assignment Dialog - Only in Edit Mode */}
                  {timetableMode === "edit" && (
                    <Dialog open={isAssignmentOpen} onOpenChange={setIsAssignmentOpen}>
                      <DialogContent className="w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                        <DialogHeader>
                          <DialogTitle className="text-sm sm:text-base">Assign Lesson</DialogTitle>
                          <DialogDescription className="text-xs sm:text-sm">
                            {selectedCell && 
                              `${sessions.find(s => s.id === selectedCell.sessionId)?.name} - ${selectedCell.day} for ${classes.find(c => c.id === currentEditingClass)?.name}`
                            }
                          </DialogDescription>
                        </DialogHeader>
                        
                        {selectedCell && (
                          <AssignmentForm
                            selectedCell={selectedCell}
                            teachers={teachers}
                            classes={classes}
                            subjects={subjects}
                            onAssign={assignLesson}
                            onRemove={removeLesson}
                            existingEntry={getTimetableEntry(selectedCell.sessionId, selectedCell.day)}
                            getConflicts={getConflicts}
                            currentEditingClass={currentEditingClass}
                          />
                        )}
                      </DialogContent>
                    </Dialog>
                  )}

                  {isConflictAnalysisOpen && (
                    <div className="absolute inset-0 z-40 bg-white/95 dark:bg-[#020817]/95 backdrop-blur-sm border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg flex flex-col">
                      <div className="flex items-start justify-between p-4 border-b border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                        <div>
                          <h2 className="text-lg font-semibold">Conflict Analysis & Subject Management</h2>
                          <p className="text-sm text-muted-foreground">Monitor scheduling conflicts, subject targets, and teacher availability.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsConflictAnalysisOpen(false)}
                          className="p-2 rounded-full hover:bg-muted"
                          aria-label="Close analysis view"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                            <CardHeader className="p-4">
                              <CardTitle className="flex items-center gap-2 text-sm">
                                <Target className="h-4 w-4" />
                                Subject Targets
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 p-4">
                              {subjects.map((subject) => (
                                <div key={subject.id} className="flex items-center justify-between border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] rounded p-2 m-2">
                                  <span className="text-sm font-medium">{subject.name}</span>
                                  <div className="flex items-center gap-2">
                                    <Input
                                      type="number"
                                      min="1"
                                      max="10"
                                      value={subjectTargets[subject.id]?.target || 0}
                                      onChange={(e) => setSubjectTargets(prev => ({
                                        ...prev,
                                        [subject.id]: { target: parseInt(e.target.value) || 0 }
                                      }))}
                                      className="w-16 h-8 text-xs border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                                    />
                                    <span className="text-xs text-muted-foreground">per week</span>
                                  </div>
                                </div>
                              ))}
                              <div className="pt-4">
                                <Button onClick={saveSubjectTargets} size="sm" className="w-full">
                                  <Save className="w-4 h-4 mr-2" />
                                  Save Targets
                                </Button>
                              </div>
                            </CardContent>
                          </Card>

                          <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                            <CardHeader className="p-4">
                              <CardTitle className="flex items-center gap-2 text-sm">
                                <BarChart3 className="h-4 w-4" />
                                Subject Analysis
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 max-h-[17.28rem] overflow-y-auto p-4">
                              {getSubjectAnalysis().map((analysis) => (
                                <div key={analysis.subjectId} className="flex items-center justify-between p-2 m-2 rounded border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                                  <span className="text-xs font-medium">{analysis.subjectName}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs">{analysis.actual}/{analysis.target}</span>
                                    <Badge
                                      variant={analysis.actual === analysis.target ? "default" : analysis.actual < analysis.target ? "destructive" : "secondary"}
                                      className="text-xs"
                                    >
                                      {analysis.actual === analysis.target ? "On Target" :
                                        analysis.actual < analysis.target ? "Under" : "Over"}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </CardContent>
                          </Card>

                          <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] lg:col-span-2">
                            <CardHeader className="p-4">
                              <CardTitle className="flex items-center gap-2 text-sm">
                                <AlertTriangle className="h-4 w-4 text-red-500" />
                                Active Conflicts
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 max-h-48 overflow-y-auto p-4">
                              {getAllTeacherConflicts().map((conflict, index) => (
                                <Alert key={index} className="border-red-200">
                                  <AlertTriangle className="h-4 w-4 text-red-500" />
                                  <AlertDescription className="text-xs">
                                    <strong className="text-red-700">{conflict.teacherName}</strong> has conflicting assignments:
                                    <div className="mt-1 space-y-1">
                                      {conflict.assignments.length > 0 ? (
                                        conflict.assignments.map((assignment, i) => (
                                          <div key={i} className="text-xs text-red-600">
                                            • {assignment.className} - {assignment.subjectName}
                                          </div>
                                        ))
                                      ) : (
                                        <div key="load" className="text-xs text-red-600">
                                          • {conflict.timeSlot} lessons per week (exceeds limit)
                                        </div>
                                      )}
                                    </div>
                                  </AlertDescription>
                                </Alert>
                              ))}
                              {getAllTeacherConflicts().length === 0 && (
                                <div className="flex items-center gap-2 text-green-600 text-xs">
                                  <Check className="h-4 w-4" />
                                  No scheduling conflicts detected
                                </div>
                              )}
                            </CardContent>
                          </Card>

                          <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] lg:col-span-2">
                            <CardHeader className="p-4">
                              <CardTitle className="flex items-center gap-2 text-sm">
                                <Users className="h-4 w-4" />
                                Teacher Availability
                                <Switch
                                  id="teacher-edit-mode"
                                  checked={teacherEditMode}
                                  onCheckedChange={setTeacherEditMode}
                                  className="data-[state=checked]:bg-blue-500"
                                />
                                <Label htmlFor="teacher-edit-mode" className="text-xs cursor-pointer">Edit</Label>
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 max-h-[28.8rem] overflow-y-auto p-4">
                              <Input
                                type="search"
                                placeholder="Search teachers..."
                                value={teacherSearch}
                                onChange={(e) => setTeacherSearch(e.target.value)}
                                className="w-full mb-3 h-8 text-xs border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                              />
                              {filteredTeachersForAvailability.map((teacher) => {
                                const avail = getTeacherAvailability()[teacher.id];
                                if (!avail) return null;
                                return (
                                  <div key={teacher.id} className="flex items-center px-2 py-1 m-2 rounded border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                                    <span className="w-1/3 text-left text-xs font-medium truncate">{teacher.name}</span>
                                    <span className="w-1/3 text-center text-xs text-muted-foreground">
                                      {teacher.employeeId ? `ID: ${teacher.employeeId}` : ''}
                                    </span>
                                    <div className="w-1/3 flex justify-end items-center gap-2">
                                      {teacherEditMode ? (
                                        <Input
                                          type="number"
                                          min="1"
                                          value={teacherLoads[teacher.id] || 25}
                                          onChange={(e) => setTeacherLoads(prev => ({
                                            ...prev,
                                            [teacher.id]: parseInt(e.target.value) || 0
                                          }))}
                                          className="w-20 h-6 text-xs border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]"
                                        />
                                      ) : (
                                        <span className="text-xs font-medium">{avail.assignedLessons}</span>
                                      )}
                                      <Badge
                                        variant={avail.actualLessons > avail.assignedLessons ? "destructive" : avail.actualLessons === avail.assignedLessons ? "default" : "secondary"}
                                        className="text-xs"
                                      >
                                        {avail.actualLessons}/{avail.assignedLessons}
                                      </Badge>
                                    </div>
                                  </div>
                                );
                              })}
                              <div className="pt-4">
                                <Button onClick={saveTeacherLoads} size="sm" className="w-full" disabled={!teacherEditMode}>
                                  <Save className="w-4 h-4 mr-2" />
                                  Save Loads
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="calendar" className="space-y-6 animate-in fade-in-50 duration-500">
                  {/* Term Setup Section - Improved Visuals */}
                  <Card className="overflow-hidden border-none shadow-md bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-950">
                    <CardHeader className="border-b border-slate-100 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 w-full">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <CalendarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <CardTitle>Academic Term Configuration</CardTitle>
                            <CardDescription>Set up term dates and details for the academic year</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                          <div className="flex flex-col items-end">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Use Recommended Dates</span>
                          </div>
                          <div className="relative">
                            <Switch
                              checked={useRecommendedDates}
                              disabled={loadingRecommendedDates}
                              onCheckedChange={async (checked) => {
                                if (schoolID) {
                                  try {
                                    await setDoc(doc(db, `schools/${schoolID}/settings/academics`), {
                                      useRecommendedDates: checked
                                    }, { merge: true });
                                  } catch (error) {
                                    console.error("Error updating settings:", error);
                                    toast({ title: "Error", description: "Failed to update settings.", variant: "destructive" });
                                  }
                                }
                              }}
                              className="data-[state=checked]:bg-green-500"
                            />
                            {loadingRecommendedDates && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Select Term</Label>
                          <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                            <SelectTrigger className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 focus:ring-blue-500">
                              <SelectValue placeholder="Select term" />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                              {terms.map((term) => (
                                <SelectItem key={term.id} value={term.id} className="focus:bg-slate-100 dark:focus:bg-slate-800 cursor-pointer">
                                  Term {term.number}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Opening Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                disabled={useRecommendedDates}
                                className="w-full justify-start text-left font-normal bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4 text-slate-500" />
                                {terms.find(t => t.id === selectedTerm)?.openingDate
                                  ? format(terms.find(t => t.id === selectedTerm)!.openingDate!, "PPP")
                                  : <span className="text-muted-foreground">Pick a date</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" align="start">
                              <Calendar
                                mode="single"
                                selected={terms.find(t => t.id === selectedTerm)?.openingDate}
                                onSelect={(date) => {
                                  setTerms(terms.map(t => 
                                    t.id === selectedTerm ? { ...t, openingDate: date } : t
                                  ));
                                }}
                                initialFocus
                                className="bg-transparent text-slate-900 dark:text-slate-100"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Closing Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                disabled={useRecommendedDates}
                                className="w-full justify-start text-left font-normal bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4 text-slate-500" />
                                {terms.find(t => t.id === selectedTerm)?.closingDate
                                  ? format(terms.find(t => t.id === selectedTerm)!.closingDate!, "PPP")
                                  : <span className="text-muted-foreground">Pick a date</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" align="start">
                              <Calendar
                                mode="single"
                                selected={terms.find(t => t.id === selectedTerm)?.closingDate}
                                onSelect={(date) => {
                                  setTerms(terms.map(t => 
                                    t.id === selectedTerm ? { ...t, closingDate: date } : t
                                  ));
                                }}
                                initialFocus
                                className="bg-transparent text-slate-900 dark:text-slate-100"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                      
                      <div className="mt-6 space-y-2">
                        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Term Title</Label>
                        <div className="flex flex-col sm:flex-row gap-4">
                          <Input
                            disabled={useRecommendedDates}
                            value={terms.find(t => t.id === selectedTerm)?.title || ""}
                            onChange={(e) => {
                              setTerms(terms.map(t => 
                                t.id === selectedTerm ? { ...t, title: e.target.value } : t
                              ));
                            }}
                            placeholder="e.g., Term 1 2025 Academic Schedule"
                            className="flex-1 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                          />
                          <Button disabled={useRecommendedDates} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm" onClick={saveTermConfiguration}>
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Event Management Section - Dramatically Improved */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Left Column: Calendar Widget */}
                    <Card className="lg:col-span-4 border-none shadow-md bg-white dark:bg-slate-950 h-fit">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Calendar View</CardTitle>
                      </CardHeader>
                      <CardContent className="flex justify-center p-4">
                        <style>{`
                          .calendar-today-day button {
                            background-color: #22c55e !important;
                            color: white !important;
                            font-weight: bold !important;
                            border-radius: 0.375rem !important;
                          }
                          ${calendarEvents.map(event => {
                            if (!event.date) return '';
                            const color = getEventColor(event.id || event.title);
                            // Parse date to get day number for class name
                            let dateObj: Date;
                            if (typeof event.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(event.date)) {
                              const [year, month, day] = event.date.split('-').map(Number);
                              dateObj = new Date(year, month - 1, day);
                            } else {
                              dateObj = new Date(event.date);
                            }
                            const dateStr = dateObj.toLocaleDateString();
                            return `
                              td:has(button[data-day="${dateStr}"]) button {
                                background-color: ${color} !important;
                                color: white !important;
                                font-weight: bold !important;
                                border-radius: 0.375rem !important;
                              }
                            `;
                          }).join('')}
                        `}</style>
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm w-full max-w-[300px] mx-auto text-slate-900 dark:text-slate-100"
                          modifiersClassNames={{
                            today: "calendar-today-day"
                          }}
                        />
                      </CardContent>
                      <div className="px-6 pb-6">
                        <div className="text-sm text-muted-foreground">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                            <span>Events scheduled</span>
                          </div>
                          <p className="text-xs mt-4">
                            Select a date on the calendar to filter events or click "Add Event" to create a new one.
                          </p>
                        </div>
                      </div>
                    </Card>

                    {/* Right Column: Events List */}
                    <Card className="lg:col-span-8 border-none shadow-md bg-white dark:bg-slate-950">
                      <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 gap-4">
                        <div>
                          <CardTitle>{showPastEvents ? "Past Events" : "Upcoming Events"}</CardTitle>
                          <CardDescription>{showPastEvents ? "View event history" : "Manage academic events and important dates"}</CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                           <div className="flex items-center space-x-2 mr-2">
                              <Switch 
                                id="past-events-mode" 
                                checked={showPastEvents}
                                onCheckedChange={setShowPastEvents}
                              />
                              <Label htmlFor="past-events-mode" className="text-sm font-medium cursor-pointer">
                                History
                              </Label>
                           </div>
                           <Dialog open={isEventDialogOpen} onOpenChange={(open) => {
                          setIsEventDialogOpen(open);
                          if (!open) {
                            setEditingEvent(null);
                            setEventForm({ title: "", type: "", date: undefined, description: "" });
                          }
                        }}>
                          <DialogTrigger asChild>
                            <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
                              <Plus className="w-4 h-4 mr-2" />
                              Add Event
                            </Button>
                          </DialogTrigger>
                          {/* Dialog Content... */}
                          <DialogContent 
                            className="max-w-md bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                          >
                            <DialogHeader>
                              <DialogTitle>{editingEvent ? "Edit Event" : "Add New Event"}</DialogTitle>
                              <DialogDescription>
                                {editingEvent ? "Update event details" : "Create a new academic event"}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 p-4">
                              <div className="space-y-2">
                                <Label>Event Title</Label>
                                <Input
                                  autoFocus={false}
                                  value={editingEvent ? editingEvent.title : eventForm.title}
                                  onChange={(e) => {
                                    if (editingEvent) {
                                      setEditingEvent({ ...editingEvent, title: e.target.value });
                                    } else {
                                      setEventForm({ ...eventForm, title: e.target.value });
                                    }
                                  }}
                                  placeholder="e.g., Parent Meeting"
                                  className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                                />
                              </div>
                              
                              <div className="space-y-2">
                                <Label>Event Type</Label>
                                <Input
                                  value={editingEvent ? editingEvent.type : eventForm.type}
                                  onChange={(e) => {
                                    if (editingEvent) {
                                      setEditingEvent({ ...editingEvent, type: e.target.value });
                                    } else {
                                      setEventForm({ ...eventForm, type: e.target.value });
                                    }
                                  }}
                                  placeholder="e.g., Exam, Meeting"
                                  className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                                />
                              </div>
                              
                              <div className="space-y-2">
                                <Label>Date</Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      className="w-full justify-start text-left font-normal bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                                    >
                                      <CalendarIcon className="mr-2 h-4 w-4" />
                                      {(editingEvent?.date || eventForm.date)
                                        ? format(new Date(editingEvent?.date || eventForm.date!), "PPP")
                                        : <span className="text-muted-foreground">Pick a date</span>}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={editingEvent?.date ? new Date(editingEvent.date) : eventForm.date}
                                      onSelect={(date) => {
                                        if (editingEvent) {
                                          setEditingEvent({ ...editingEvent, date: date ? date.toISOString() : "" });
                                        } else {
                                          setEventForm({ ...eventForm, date });
                                        }
                                      }}
                                      initialFocus
                                      className="bg-transparent text-slate-900 dark:text-slate-100"
                                    />
                                  </PopoverContent>
                                </Popover>
                              </div>
                              
                              <div className="space-y-2">
                                <Label>Description (Optional)</Label>
                                <Textarea
                                  value={editingEvent ? editingEvent.description : eventForm.description}
                                  onChange={(e) => {
                                    if (editingEvent) {
                                      setEditingEvent({ ...editingEvent, description: e.target.value });
                                    } else {
                                      setEventForm({ ...eventForm, description: e.target.value });
                                    }
                                  }}
                                  placeholder="Event description"
                                  rows={3}
                                  className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                                />
                              </div>
                              
                              <Button 
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-2"
                                onClick={handleSaveEvent}
                              >
                                {editingEvent ? "Update Event" : "Create Event"}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                        </div>
                      </CardHeader>
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          {calendarEvents.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-900/50">
                              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-full mb-3">
                                <CalendarIcon className="w-6 h-6 text-slate-400" />
                              </div>
                              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">No events scheduled</h3>
                              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mx-auto mt-1">
                                Get started by clicking "Add Event" to create your first academic event.
                              </p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {calendarEvents
                                .filter(event => {
                                  if (!event.date) return false;
                                  const eventDate = new Date(event.date);
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  if (showPastEvents) {
                                     return eventDate < today;
                                  }
                                  return eventDate >= today;
                                })
                                .sort((a, b) => {
                                   const dateA = new Date(a.date).getTime();
                                   const dateB = new Date(b.date).getTime();
                                   return showPastEvents ? dateB - dateA : dateA - dateB;
                                })
                                .map((event) => {
                                const eventColor = getEventColor(event.id || event.title);
                                return (
                                <div key={event.id} className="group relative p-5 border border-slate-200 dark:border-slate-800 rounded-xl hover:shadow-md transition-all duration-200 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700">
                                  <div 
                                    className="absolute top-0 left-0 w-1 h-full rounded-l-xl opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{ backgroundColor: eventColor }}
                                  ></div>
                                  <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center gap-2">
                                      <Badge 
                                        variant="outline" 
                                        className="border-opacity-50"
                                        style={{ 
                                          backgroundColor: `${eventColor}15`,
                                          color: eventColor,
                                          borderColor: `${eventColor}40`
                                        }}
                                      >
                                        {event.type || "Event"}
                                      </Badge>
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => {
                                          setEditingEvent(event);
                                          setEventForm({
                                            title: event.title,
                                            type: event.type || "",
                                            date: new Date(event.date),
                                            description: event.description,
                                          });
                                          setIsEventDialogOpen(true);
                                        }}
                                        className="h-8 w-8 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        onClick={async () => {
                                          if (confirm("Are you sure you want to delete this event?")) {
                                            await deleteEvent(event.id);
                                            setCalendarEvents(prev => prev.filter(e => e.id !== event.id));
                                          }
                                        }}
                                        className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  <h4 className="font-semibold text-lg text-slate-900 dark:text-slate-100 mb-2">{event.title}</h4>
                                  
                                  {event.date && (
                                    <div className="flex items-center text-sm text-slate-500 dark:text-slate-400 mb-2">
                                      <CalendarIcon className="w-4 h-4 mr-2" style={{ color: eventColor }} />
                                      {format(new Date(event.date), "EEEE, MMMM do, yyyy")}
                                    </div>
                                  )}
                                  
                                  {event.description && (
                                    <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 mt-2 pl-6 border-l-2 border-slate-100 dark:border-slate-800">
                                      {event.description}
                                    </p>
                                  )}
                                </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="grading">
                  <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div>
                      <h2 className="text-lg sm:text-xl font-semibold text-foreground">Advanced Grading System</h2>
                      <p className="text-xs sm:text-sm text-muted-foreground">Comprehensive grading, analytics, and report generation</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <Select value={gradingView} onValueChange={setGradingView}>
                        <SelectTrigger className="w-full sm:w-48 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] dark:[&_[data-state=hovered]]:bg-[#1E293B]">
                          <SelectItem value="structure">Grade Structure</SelectItem>
                          <SelectItem value="analytics">Performance Analytics</SelectItem>
                          <SelectItem value="rankings">Rankings</SelectItem>
                          <SelectItem value="reports">Report Generation</SelectItem>
                          <SelectItem value="entry">Grade Entry</SelectItem>
                          <SelectItem value="sessions">Exam Session</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-md border border-green-700 dark:bg-green-700 dark:hover:bg-green-800 dark:border-green-800 transition-colors duration-200" onClick={() => setShowReportPreview(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Report Form Layout
                      </Button>
                      <Button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-md border border-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 dark:border-blue-800 transition-colors duration-200 ml-2" onClick={() => setShowCategoryDialog(true)}>
                        <Settings className="w-4 h-4 mr-2" />
                        Set Exam Categories
                      </Button>
                          {/* Set Exam Categories Dialog */}
                          <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
                            <DialogContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] max-w-3xl">
                              <DialogHeader>
                                <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                                  Manage Exam Categories
                                </DialogTitle>
                                <DialogDescription className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                  Configure exam categories globally or per class
                                </DialogDescription>
                              </DialogHeader>
                              
                              <div className="space-y-4 py-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Subject</Label>
                                    <Select value={categorySubjectId} onValueChange={setCategorySubjectId}>
                                      <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]">
                                        <SelectValue placeholder="Select Subject" />
                                      </SelectTrigger>
                                      <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                        {subjects.map(subj => (
                                          <SelectItem key={subj.id} value={subj.id} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31]">
                                            {subj.subjectName || subj.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Scope</Label>
                                    <div className="flex gap-2">
                                      <Button 
                                        variant={categoryScope === "global" ? "default" : "outline"} 
                                        size="sm" 
                                        onClick={() => setCategoryScope("global")}
                                        className={categoryScope === "global" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                                      >
                                        Global (All Classes)
                                      </Button>
                                      <Button 
                                        variant={categoryScope === "class" ? "default" : "outline"} 
                                        size="sm" 
                                        onClick={() => setCategoryScope("class")}
                                        className={categoryScope === "class" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                                      >
                                        Specific Class
                                      </Button>
                                    </div>
                                  </div>
                                </div>

                                {categoryScope === "class" && (
                                  <div className="space-y-2">
                                    <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Forms (Select Multiple)</Label>
                                    <div className="border border-[#E2E8F0] dark:border-[#1E293B] rounded-md p-3 bg-white dark:bg-[#020817] max-h-48 overflow-y-auto">
                                      {Array.from(new Set(
                                        classes
                                          .map(cls => {
                                            const name = cls.className || cls.name || '';
                                            const words = name.split(' ');
                                            const streamNames = ['blue', 'red', 'green', 'yellow', 'purple', 'orange', 'pink', 'white', 'black', 'east', 'west', 'north', 'south', 'a', 'b', 'c', 'd', 'e'];
                                            const lastWord = words[words.length - 1]?.toLowerCase();
                                            if (words.length > 1 && streamNames.includes(lastWord)) {
                                              return words.slice(0, -1).join(' ');
                                            }
                                            return name;
                                          })
                                          .filter(Boolean)
                                      )).sort().map(form => (
                                        <div key={form} className="flex items-center space-x-2 py-1.5 hover:bg-gray-50 dark:hover:bg-[#131E31] rounded px-2">
                                          <Checkbox
                                            id={`form-${form}`}
                                            checked={categoryClassId.includes(form)}
                                            onCheckedChange={(checked) => {
                                              if (checked) {
                                                setCategoryClassId([...categoryClassId, form]);
                                              } else {
                                                setCategoryClassId(categoryClassId.filter(id => id !== form));
                                              }
                                            }}
                                            className="border-[#E2E8F0] dark:border-[#1E293B]"
                                          />
                                          <Label
                                            htmlFor={`form-${form}`}
                                            className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer flex-1"
                                          >
                                            {form}
                                          </Label>
                                        </div>
                                      ))}
                                    </div>
                                    {categoryClassId.length > 0 && (
                                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        Selected: {categoryClassId.join(', ')}
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div className="rounded-md border border-[#E2E8F0] dark:border-[#1E293B]">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Category Name</TableHead>
                                        <TableHead className="w-[100px]">Max Marks</TableHead>
                                        <TableHead className="w-[100px]">Weight (%)</TableHead>
                                        <TableHead className="w-[50px]"></TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {categoryList.map((cat, index) => {
                                        return (
                                          <TableRow key={index}>
                                            <TableCell>
                                              <Input
                                                value={cat.name}
                                                onChange={(e) => {
                                                  const newCats = [...categoryList];
                                                  newCats[index].name = e.target.value;
                                                  setCategoryList(newCats);
                                                }}
                                                placeholder="e.g. Paper 1"
                                                className="h-8"
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Input
                                                type="number"
                                                value={cat.maxMarks}
                                                onChange={(e) => {
                                                  const newCats = [...categoryList];
                                                  newCats[index].maxMarks = Number(e.target.value);
                                                  setCategoryList(newCats);
                                                }}
                                                className="h-8"
                                                min="1"
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Input
                                                type="number"
                                                value={cat.weight}
                                                onChange={(e) => {
                                                  const newCats = [...categoryList];
                                                  newCats[index].weight = Number(e.target.value);
                                                  setCategoryList(newCats);
                                                }}
                                                className="h-8"
                                                min="0"
                                                max="100"
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-red-500 hover:text-red-600"
                                                onClick={() => {
                                                  const newCats = categoryList.filter((_, i) => i !== index);
                                                  setCategoryList(newCats);
                                                }}
                                              >
                                                <Trash2 className="h-4 w-4" />
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </div>
                                
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setCategoryList([...categoryList, { name: '', maxMarks: 100, weight: 0 }])}
                                  className="w-full border-dashed mt-2"
                                >
                                  <Plus className="mr-2 h-4 w-4" /> Add Category
                                </Button>

                                <div className="flex justify-between text-sm text-muted-foreground px-2">
                                  <span>Total Marks: {categoryList.reduce((sum, c) => sum + (c.maxMarks || 0), 0)}</span>
                                  <span className={Math.abs(categoryList.reduce((sum, c) => sum + (c.weight || 0), 0) - 100) > 0.1 ? "text-red-500 font-bold" : "text-green-600 font-bold"}>
                                    Total Weight: {categoryList.reduce((sum, c) => sum + (c.weight || 0), 0)}% / 100%
                                  </span>
                                </div>
                              </div>

                              <div className="flex gap-3 justify-end">
                                <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
                                  Cancel
                                </Button>
                                <Button 
                                  onClick={() => {/* TODO: Save logic here */}} 
                                  className="bg-[#155DFC] hover:bg-[#1148CA] text-white"
                                >
                                  Save Configuration
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                    </div>
                  </div>

                  {gradingView === "structure" && <GradingStructure />}
                  {gradingView === "analytics" && <PerformanceAnalytics />}
                  {gradingView === "rankings" && <RankingSystem />}
                  {gradingView === "reports" && <ReportGeneration />}
                  {gradingView === "entry" && <SharedGradingDashboard userRole="admin" schoolID={schoolID || undefined} />}
                  {gradingView === "sessions" && <ExamSessionManagement />}
                  
                  {/* Report Preview moved to main view */}
                  {gradingView === "grades" && (
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader className="p-4">
                        <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                          <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                          Grade Entry & Management
                        </CardTitle>
                        <CardDescription>Enter and manage student grades efficiently</CardDescription>
                      </CardHeader>
                      <CardContent className="p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <Button 
                            variant="outline" 
                            className="h-24 flex-col gap-2 border border-[#E2E8F0] dark:border-[#1E293B]"
                            onClick={() => setGradingView("entry")}
                          >
                            <Plus className="w-6 h-6" />
                            <span>Grade Entry Module</span>
                          </Button>
                          <Button variant="outline" className="h-24 flex-col gap-2 border border-[#E2E8F0] dark:border-[#1E293B]">
                            <Download className="w-6 h-6" />
                            <span>Bulk Import Grades</span>
                          </Button>
                          <Button variant="outline" className="h-24 flex-col gap-2 border border-[#E2E8F0] dark:border-[#1E293B]">
                            <Edit className="w-6 h-6" />
                            <span>Review & Moderate</span>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="analytics" className="space-y-6 animate-in fade-in-50 duration-500">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white/40 dark:bg-slate-900/40 backdrop-blur-md p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
                    <div>
                      <h2 className="text-xl font-black text-foreground tracking-tight">Academic Analytics</h2>
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Comprehensive performance insights and trends</p>
                    </div>
                    <div className="flex items-center gap-2">
                       <Button variant="outline" size="sm" className="text-xs font-bold border-slate-200 dark:border-slate-800 h-9">
                          <Download className="h-3.5 w-3.5 mr-2" /> Export Data
                       </Button>
                       <Button size="sm" className="text-xs font-bold bg-[#3879E3] hover:bg-[#3879E3]/90 h-9">
                          <Target className="h-3.5 w-3.5 mr-2" /> Set Goals
                       </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="group relative overflow-hidden border border-slate-200 dark:border-slate-800 bg-[#FFFFFF] dark:bg-[#020817] transition-all duration-300 hover:shadow-2xl">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
                      <CardHeader className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="flex items-center gap-2 text-base font-bold">
                              <BarChart3 className="h-5 w-5 text-blue-500" />
                              Subject Performance
                            </CardTitle>
                            <CardDescription className="text-xs font-medium mt-1">Average scores by subject across all grades</CardDescription>
                          </div>
                          <Badge variant="secondary" className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-none font-bold text-[10px] uppercase">Live Data</Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="px-6 pb-6 pt-0">
                        <ChartContainer
                          config={{
                            average: {
                              label: "Average Score",
                              color: "hsl(221, 83%, 53%)",
                            },
                          }}
                          className="h-[250px] sm:h-[300px]"
                        >
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={performanceData}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.1} />
                              <XAxis dataKey="subject" fontSize={11} fontWeight={600} tickLine={false} axisLine={false} dy={10} />
                              <YAxis fontSize={11} fontWeight={600} tickLine={false} axisLine={false} dx={-10} />
                              <ChartTooltip cursor={{fill: 'rgba(56, 121, 227, 0.05)'}} content={<ChartTooltipContent />} />
                              <Bar dataKey="average" fill="var(--color-average)" radius={[6, 6, 0, 0]} barSize={32} />
                            </BarChart>
                          </ResponsiveContainer>
                        </ChartContainer>
                      </CardContent>
                    </Card>

                    <Card className="group relative overflow-hidden border border-slate-200 dark:border-slate-800 bg-[#FFFFFF] dark:bg-[#020817] transition-all duration-300 hover:shadow-2xl">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-teal-500"></div>
                      <CardHeader className="p-6">
                         <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="flex items-center gap-2 text-base font-bold">
                              <TrendingUp className="h-5 w-5 text-emerald-500" />
                              Monthly Progress Trend
                            </CardTitle>
                            <CardDescription className="text-xs font-medium mt-1">School-wide performance metrics over time</CardDescription>
                          </div>
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold uppercase">
                             <TrendingUp className="h-3 w-3" /> +4.2% Growth
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="px-6 pb-6 pt-0">
                        <ChartContainer
                          config={{
                            performance: {
                              label: "Performance",
                              color: "hsl(142, 71%, 45%)",
                            },
                          }}
                          className="h-[250px] sm:h-[300px]"
                        >
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={monthlyProgress} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.1} />
                              <XAxis dataKey="month" fontSize={11} fontWeight={600} tickLine={false} axisLine={false} dy={10} />
                              <YAxis fontSize={11} fontWeight={600} tickLine={false} axisLine={false} dx={-10} />
                              <ChartTooltip content={<ChartTooltipContent />} />
                              <Line type="monotone" dataKey="performance" stroke="var(--color-performance)" strokeWidth={4} dot={{ r: 6, fill: "var(--color-performance)", strokeWidth: 2, stroke: "#fff" }} activeDot={{ r: 8, strokeWidth: 0 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </ChartContainer>
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="group relative overflow-hidden border border-slate-200 dark:border-slate-800 bg-[#FFFFFF] dark:bg-[#020817] transition-all duration-300 hover:shadow-2xl">
                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                    <CardHeader className="p-6 border-b border-slate-50 dark:border-slate-900/50">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg font-bold">Top Staff Contributors</CardTitle>
                          <CardDescription className="text-xs font-medium mt-1">Highest rated educators based on performance metrics</CardDescription>
                        </div>
                        <Button variant="ghost" size="sm" className="text-xs font-bold text-blue-600 dark:text-blue-400 h-8 px-3">
                           View Full Ranking
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {teacherPerformance.map((teacher, index) => (
                          <div key={index} className="group/item relative flex flex-col p-4 rounded-2xl border border-slate-100 dark:border-slate-800/60 bg-slate-50/50 dark:bg-white/[0.01] hover:bg-white dark:hover:bg-white/[0.04] transition-all duration-300">
                            <div className="flex items-start justify-between mb-4">
                              <div className={`w-12 h-12 rounded-2xl ${teacher.color} flex items-center justify-center text-white shadow-lg group-hover/item:scale-110 transition-transform duration-300`}>
                                <teacher.icon className="h-6 w-6" />
                              </div>
                              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-black">
                                {teacher.rating} <span className="text-[10px]">★</span>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <p className="font-black text-foreground text-base tracking-tight leading-none">{teacher.name}</p>
                              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">{teacher.subject}</p>
                            </div>
                            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/60 flex items-center justify-between">
                               <div className="flex items-center gap-1 text-[10px] font-bold text-muted-foreground italic">
                                  <Users className="h-3 w-3" />
                                  {teacher.students} students mentored
                               </div>
                               <div className="w-8 h-8 rounded-full bg-primary/5 flex items-center justify-center translate-x-2 opacity-0 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all duration-300">
                                  <Plus className="h-4 w-4 text-primary" />
                               </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
              )}
            </TooltipProvider>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

// Enhanced Assignment Form Component with proper typing
interface AssignmentFormProps {
  selectedCell: {sessionId: number, day: string};
  teachers: Array<{id: string, name: string, subject: string, employeeId?: string}>;
  classes: Array<{id: string, name: string}>;
  subjects: Array<{id: string, name: string}>;
  onAssign: (sessionId: number, day: string, assignment: TimetableEntry) => void;
  onRemove: (sessionId: number, day: string) => void;
  existingEntry: TimetableEntry | null;
  getConflicts: (sessionId: number, day: string, teacherId: string, classId: string) => string[];
  currentEditingClass: string;
}

const AssignmentForm: React.FC<AssignmentFormProps> = ({ 
  selectedCell, 
  teachers, 
  classes, 
  subjects, 
  onAssign, 
  onRemove, 
  existingEntry, 
  getConflicts, 
  currentEditingClass 
}) => {

  const [formData, setFormData] = useState({
    teacherId: existingEntry?.teacherId || '',
    classId: currentEditingClass || '',
    subjectId: existingEntry?.subjectId || '',
  });

  // Keep formData.classId in sync with currentEditingClass
  useEffect(() => {
    if (currentEditingClass && formData.classId !== currentEditingClass) {
      setFormData(f => ({ ...f, classId: currentEditingClass }));
    }
  }, [currentEditingClass]);

  const [conflicts, setConflicts] = useState<string[]>([]);

  const handleInputChange = (field: string, value: string) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // Check for conflicts in real-time
    if (newFormData.teacherId && newFormData.classId) {
      const newConflicts = getConflicts(selectedCell.sessionId, selectedCell.day, newFormData.teacherId, newFormData.classId);
      setConflicts(newConflicts);
    }
  };

  const handleSubmit = () => {
    if (formData.teacherId && formData.classId && formData.subjectId) {
      const teacher = teachers.find(t => t.id === formData.teacherId);
      const cls = classes.find(c => c.id === formData.classId);
      const subject = subjects.find(s => s.id === formData.subjectId);
      
      if (teacher && cls && subject) {
        const assignment: TimetableEntry = {
          teacherId: formData.teacherId,
          teacherName: teacher.name,
          classId: formData.classId,
          className: cls.name,
          subjectId: formData.subjectId,
          subjectName: subject.name,
        };
        
        onAssign(selectedCell.sessionId, selectedCell.day, assignment);
      }
    }
  };

  const handleRemove = () => {
    onRemove(selectedCell.sessionId, selectedCell.day);
  };

  return (
    <div className="space-y-4 p-4">
      {conflicts.length > 0 && (
        <Alert className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Conflicts detected:</strong>
            <ul className="mt-2 list-disc list-inside">
              {conflicts.map((conflict, index) => (
                <li key={index} className="text-sm">{conflict}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div>
        <Label htmlFor="subject">Subject</Label>
        <Select value={formData.subjectId} onValueChange={(value) => handleInputChange('subjectId', value)}>
          <SelectTrigger className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
            <SelectValue placeholder="Select subject" />
          </SelectTrigger>
          <SelectContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] dark:[&_[data-state=hovered]]:bg-[#1E293B]">
            {subjects.map((subject) => (
              <SelectItem key={subject.id} value={subject.id}>{subject.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="teacher">Teacher</Label>
        <Select value={formData.teacherId} onValueChange={(value) => handleInputChange('teacherId', value)}>
          <SelectTrigger className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
            <SelectValue placeholder="Select teacher" />
          </SelectTrigger>
          <SelectContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] dark:[&_[data-state=hovered]]:bg-[#1E293B]">
            {teachers.map((teacher) => (
              <SelectItem key={teacher.id} value={teacher.id}>
                {teacher.name}{teacher.employeeId ? ` (${teacher.employeeId})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2 mt-2">
        <Button
          onClick={handleSubmit}
          disabled={!formData.teacherId || !formData.classId || !formData.subjectId || conflicts.length > 0}
          className={
            `flex-1 transition-colors duration-200 ` +
            (
              !formData.teacherId || !formData.subjectId || conflicts.length > 0
                ? 'bg-blue-400 hover:bg-blue-400 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            )
          }
        >
          {existingEntry ? 'Update' : 'Assign'} Lesson
        </Button>
        {existingEntry && (
          <Button variant="outline" onClick={handleRemove} className="border border-[#E2E8F0] dark:border-[#1E293B]">
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default AcademicsContent;